import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { api, formatPrice, API } from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Package, Key, Users, FileText, Megaphone, Mail, Plus, Trash2, Upload, Download, Edit, X, Home as HomeIcon, PanelTop, BookOpen, Copy, ImageUp, FolderTree, ChevronRight, ChevronDown, Link as LinkIcon, ExternalLink, Tag, Search, Ticket, Ban, Receipt, Gamepad2 } from "lucide-react";
import SEO from "../components/SEO";
import RichTextEditor from "../components/RichTextEditor";
import GamingProductsAdmin from "../components/admin/GamingProductsAdmin";
import GamingSettingsAdmin from "../components/admin/GamingSettingsAdmin";

function ImageUploader({ onUploaded, label = "Nahrát obrázek", accept = "image/*" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Backend may return either an absolute URL (R2 public URL) or a
      // relative path like /api/uploads/... — handle both correctly.
      const raw = r.data?.url || "";
      const finalUrl = /^https?:\/\//i.test(raw) ? raw : `${API.replace(/\/api$/, "")}${raw}`;
      onUploaded({ ...r.data, url: finalUrl });
      toast.success("Soubor nahrán");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nepodařilo se nahrát");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`inline-flex items-center gap-1 cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:bg-zinc-50 ${busy ? "opacity-60 pointer-events-none" : ""}`}>
      <ImageUp className="w-3.5 h-3.5"/>
      {busy ? "Nahrávám…" : label}
      <input type="file" accept={accept} className="hidden" onChange={handle} data-testid="img-upload-input"/>
    </label>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) navigate("/prihlaseni");
  }, [user, ready, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-8 pb-20" data-testid="admin-page">
      <SEO title="Administrace" noindex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-1">Admin</div>
          <h1 className="font-display text-4xl font-bold">Administrace</h1>
        </div>
        <div className="flex gap-1 overflow-x-auto scrollbar-hide border-b border-zinc-200 mb-8">
          <T id="dashboard" cur={tab} set={setTab} icon={LayoutDashboard}>Dashboard</T>
          <T id="homepage" cur={tab} set={setTab} icon={HomeIcon}>Hlavní stránka</T>
          <T id="homesections" cur={tab} set={setTab} icon={PanelTop}>Sekce katalogu</T>
          <T id="site" cur={tab} set={setTab} icon={PanelTop}>Záhlaví & Zápatí</T>
          <T id="pages" cur={tab} set={setTab} icon={BookOpen}>Stránky & FAQ</T>
          <T id="seo" cur={tab} set={setTab} icon={Search}>SEO</T>
          <T id="vouchers" cur={tab} set={setTab} icon={Tag}>Slevové kódy</T>
          <T id="redeem" cur={tab} set={setTab} icon={Ticket}>Poukazy (Aukro)</T>
          <T id="invoices" cur={tab} set={setTab} icon={Receipt}>Faktury (ruční)</T>
          <T id="gaming" cur={tab} set={setTab} icon={Gamepad2}>Gaming produkty</T>
          <T id="gamingpage" cur={tab} set={setTab} icon={Gamepad2}>Gaming stránka</T>
          <T id="emails" cur={tab} set={setTab} icon={Mail}>E-mail logy</T>
          <T id="categories" cur={tab} set={setTab} icon={FolderTree}>Kategorie</T>
          <T id="products" cur={tab} set={setTab} icon={Package}>Produkty</T>
          <T id="keys" cur={tab} set={setTab} icon={Key}>Klíče</T>
          <T id="downloads" cur={tab} set={setTab} icon={Download}>Soubory ke stažení</T>
          <T id="guides" cur={tab} set={setTab} icon={BookOpen}>Návody</T>
          <T id="orders" cur={tab} set={setTab} icon={FileText}>Objednávky</T>
          <T id="customers" cur={tab} set={setTab} icon={Users}>Zákazníci</T>
          <T id="blog" cur={tab} set={setTab} icon={FileText}>Blog</T>
          <T id="banners" cur={tab} set={setTab} icon={Megaphone}>Bannery</T>
          <T id="newsletter" cur={tab} set={setTab} icon={Mail}>Newsletter</T>
        </div>
        {tab === "dashboard" && <Dashboard/>}
        {tab === "homepage" && <Homepage/>}
        {tab === "homesections" && <HomeSections/>}
        {tab === "site" && <SiteHeaderFooter/>}
        {tab === "pages" && <CmsPages/>}
        {tab === "seo" && <SeoSettings/>}
        {tab === "vouchers" && <Vouchers/>}
        {tab === "redeem" && <RedeemVouchers/>}
        {tab === "invoices" && <ManualInvoices/>}
        {tab === "gaming" && <GamingProductsAdmin/>}
        {tab === "gamingpage" && <GamingSettingsAdmin/>}
        {tab === "emails" && <EmailLogs/>}
        {tab === "categories" && <Categories/>}
        {tab === "products" && <Products/>}
        {tab === "keys" && <Keys/>}
        {tab === "downloads" && <DownloadVault/>}
        {tab === "guides" && <Guides/>}
        {tab === "orders" && <Orders/>}
        {tab === "customers" && <Customers/>}
        {tab === "blog" && <Blog/>}
        {tab === "banners" && <Banners/>}
        {tab === "newsletter" && <Newsletter/>}
      </div>
    </div>
  );
}

function T({ id, cur, set, icon: Icon, children }) {
  return (
    <button onClick={() => set(id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${cur === id ? "border-blue-600 text-blue-600" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid={`admin-tab-${id}`}>
      <Icon className="w-4 h-4"/>{children}
    </button>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [purging, setPurging] = useState(false);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  const fixImages = async () => {
    if (!window.confirm("Opraví dvojitě prefixované obrázky a přesune lokální soubory do R2. Pokračovat?")) return;
    setFixing(true);
    try {
      const r1 = await api.post("/admin/fix-double-prefixed-urls");
      const r2 = await api.post("/admin/migrate-uploads-to-r2");
      const totalFixed = Object.values(r1.data.fixed || {}).reduce((a, b) => a + b, 0);
      const totalMigrated = Object.values(r2.data.migrated || {}).reduce((a, b) => a + b, 0);
      const missing = r2.data.missing_count || 0;
      toast.success(`Hotovo! Opraveno ${totalFixed} záznamů, migrováno ${totalMigrated}. ${missing > 0 ? `Chybí ${missing} souborů (nahrajte je znovu).` : ""}`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oprava selhala");
    } finally {
      setFixing(false);
    }
  };

  const purgeSeedKeys = async () => {
    if (!window.confirm("Smaže pouze testovací klíče (vygenerované při deployi, ještě nebyly prodány). Vaše skutečně nahrané klíče zůstanou. Pokračovat?")) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-seed-license-keys");
      toast.success(`Smazáno ${r.data.deleted || 0} testovacích klíčů.`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Smazání selhalo");
    } finally {
      setPurging(false);
    }
  };

  if (!stats) return <div>Načítání…</div>;
  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Tržby celkem" value={formatPrice(stats.total_revenue)} color="from-blue-500 to-indigo-600"/>
        <Stat label="Objednávky" value={stats.total_orders} sub={`${stats.paid_orders} zaplacených`}/>
        <Stat label="Zákazníci" value={stats.total_customers}/>
        <Stat label="Produkty" value={stats.total_products}/>
        <Stat label="Klíče k dispozici" value={stats.keys_available}/>
        <Stat label="Klíče odeslané" value={stats.keys_assigned}/>
      </div>
      <div className="mt-8 p-6 rounded-2xl bg-amber-50 border border-amber-200">
        <div className="font-display font-semibold text-zinc-900 mb-1">Údržba</div>
        <p className="text-sm text-zinc-700 mb-4">Nástroje pro úklid po nasazení nové verze.</p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={fixImages}
            disabled={fixing}
            className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-fix-images-btn"
          >
            {fixing ? "Opravuji…" : "🛠️ Opravit obrázky"}
          </button>
          <button
            onClick={purgeSeedKeys}
            disabled={purging}
            className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-purge-seed-keys-btn"
          >
            {purging ? "Mažu…" : "🧹 Smazat testovací klíče"}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-3">Tlačítko „Smazat testovací klíče" odstraní pouze automaticky vygenerované klíče se značkou „Seed key", které nebyly nikdy přiřazené k objednávce. Vaše skutečně nahrané klíče nemůže ovlivnit.</p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className={`rounded-2xl p-6 ${color ? `bg-gradient-to-br ${color} text-white` : "bg-white border border-zinc-200"}`}>
      <div className={`text-xs uppercase tracking-widest ${color ? "text-white/80" : "text-zinc-500"} mb-2`}>{label}</div>
      <div className={`font-display font-bold text-3xl ${color ? "text-white" : "text-zinc-900"}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${color ? "text-white/70" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

function Homepage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  useEffect(() => {
    api.get("/site-settings").then((r) => setForm(r.data));
    api.get("/blog").then((r) => setBlogPosts(r.data || [])).catch(() => setBlogPosts([]));
  }, []);
  if (!form) return <div>Načítání…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBlogPost = (id) => {
    const cur = form.home_blog_post_ids || [];
    if (cur.includes(id)) {
      set("home_blog_post_ids", cur.filter((x) => x !== id));
    } else if (cur.length < 3) {
      set("home_blog_post_ids", [...cur, id]);
    } else {
      toast.error("Vyberte maximálně 3 příspěvky.");
    }
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", form);
      toast.success("Hlavní stránka aktualizována. Obnovte hlavní stránku pro náhled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="grid lg:grid-cols-5 gap-6" data-testid="admin-homepage">
      <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-2xl p-6 space-y-4">
        <div className="font-display font-bold text-lg mb-2">Hero – text</div>
        <FF label="Štítek nad titulkem"><input value={form.hero_badge || ""} onChange={(e) => set("hero_badge", e.target.value)} className={I} data-testid="hp-badge"/></FF>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Titulek – řádek 1"><input value={form.hero_title_line1 || ""} onChange={(e) => set("hero_title_line1", e.target.value)} className={I} placeholder="Digital Software" data-testid="hp-title-1"/></FF>
          <FF label="Titulek – řádek 2"><input value={form.hero_title_line2 || ""} onChange={(e) => set("hero_title_line2", e.target.value)} className={I} placeholder="Made" data-testid="hp-title-2"/></FF>
          <FF label="Titulek – zvýrazněné slovo (gradient)"><input value={form.hero_title_highlight || ""} onChange={(e) => set("hero_title_highlight", e.target.value)} className={I} placeholder="Simple" data-testid="hp-title-hl"/></FF>
          <FF label="Titulek – řádek 3"><input value={form.hero_title_line3 || ""} onChange={(e) => set("hero_title_line3", e.target.value)} className={I} placeholder="and Secure" data-testid="hp-title-3"/></FF>
        </div>
        <FF label="Podtitulek"><textarea value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} rows={2} className={I} data-testid="hp-sub"/></FF>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Tlačítko 1 (modré)"><input value={form.hero_cta1 || ""} onChange={(e) => set("hero_cta1", e.target.value)} className={I} data-testid="hp-cta1"/></FF>
          <FF label="Tlačítko 2 (sekundární)"><input value={form.hero_cta2 || ""} onChange={(e) => set("hero_cta2", e.target.value)} className={I} data-testid="hp-cta2"/></FF>
        </div>
        <FF label="Hero obrázek – URL">
          <div className="flex gap-2 items-center">
            <input value={form.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={I} placeholder="https://..." data-testid="hp-image"/>
            <ImageUploader onUploaded={(f) => set("hero_image", f.url)} label="Nahrát"/>
          </div>
        </FF>
        <p className="text-xs text-zinc-500">Tip: použijte přímý odkaz na obrázek (např. z vašeho úložiště nebo Unsplash). Doporučený poměr stran 4:3 a šířka min. 1200 px.</p>
        <FF label="Hero – odznaky pod tlačítky (oddělené znakem |)"><input value={form.home_hero_features || ""} onChange={(e) => set("home_hero_features", e.target.value)} className={I} placeholder="Originální licence|Faktura PDF|CZ/SK podpora" data-testid="hp-features"/></FF>

        <div className="pt-4 mt-4 border-t border-zinc-200">
          <div className="font-display font-bold text-lg mb-3">Sekce „Proč nás vybrat"</div>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Štítek (malý nadpis)"><input value={form.home_why_subtitle || ""} onChange={(e) => set("home_why_subtitle", e.target.value)} className={I} data-testid="hp-why-subtitle"/></FF>
            <FF label="Hlavní nadpis sekce"><input value={form.home_why_title || ""} onChange={(e) => set("home_why_title", e.target.value)} className={I} data-testid="hp-why-title"/></FF>
          </div>
          {[1,2,3,4,5].map(n => (
            <div key={n} className="grid grid-cols-3 gap-3 mt-3">
              <FF label={`Blok ${n} – nadpis`}><input value={form[`home_why_b${n}_title`] || ""} onChange={(e) => set(`home_why_b${n}_title`, e.target.value)} className={I} data-testid={`hp-why-b${n}-title`}/></FF>
              <div className="col-span-2"><FF label={`Blok ${n} – text`}><textarea value={form[`home_why_b${n}_text`] || ""} onChange={(e) => set(`home_why_b${n}_text`, e.target.value)} rows={2} className={I} data-testid={`hp-why-b${n}-text`}/></FF></div>
            </div>
          ))}
        </div>

        <div className="pt-4 mt-4 border-t border-zinc-200">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg">Sekce „Z našeho blogu"</div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.home_blog_enabled} onChange={(e) => set("home_blog_enabled", e.target.checked)} className="w-4 h-4" data-testid="hp-blog-enabled"/>
              <span>Zobrazit na hlavní stránce</span>
            </label>
          </div>
          <p className="text-xs text-zinc-500 mb-3">Vyberte 1–3 příspěvky, které se zobrazí vedle sebe s obrázkem, názvem a krátkým popisem. Pokud nic nevyberete, ukážeme automaticky 3 nejnovější publikované.</p>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Nadpis sekce"><input value={form.home_blog_title || ""} onChange={(e) => set("home_blog_title", e.target.value)} className={I} placeholder="Z našeho blogu" data-testid="hp-blog-title"/></FF>
            <FF label="Pozice (za kolikátou řadou produktů)"><input type="number" min="0" max="20" value={form.home_blog_position ?? 2} onChange={(e) => set("home_blog_position", parseInt(e.target.value) || 0)} className={I} data-testid="hp-blog-position"/></FF>
          </div>
          <FF label="Podtitulek (nepovinné)"><input value={form.home_blog_subtitle || ""} onChange={(e) => set("home_blog_subtitle", e.target.value)} className={I} placeholder="Tipy, návody a novinky ze světa softwaru." data-testid="hp-blog-subtitle"/></FF>
          <div className="mt-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Vybrané příspěvky ({(form.home_blog_post_ids || []).length}/3)</div>
            {blogPosts.length === 0 ? (
              <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-xl px-4 py-6 text-center">Zatím žádné příspěvky. Vytvořte je v <strong>Admin → Blog</strong>.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="hp-blog-posts">
                {blogPosts.map((p) => {
                  const idx = (form.home_blog_post_ids || []).indexOf(p.id);
                  const picked = idx >= 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleBlogPost(p.id)}
                      className={`text-left p-3 rounded-xl border transition-all ${picked ? "border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/20" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                      data-testid={`hp-blog-post-${p.slug}`}
                    >
                      <div className="flex items-start gap-3">
                        {p.cover_image && <img src={p.cover_image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" onError={(e) => e.currentTarget.style.display = "none"}/>}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm truncate">{p.title}</div>
                          <div className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{p.excerpt}</div>
                        </div>
                        {picked && <div className="text-xs font-bold text-blue-600 bg-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 border border-blue-200">{idx + 1}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="hp-save">{saving ? "Ukládám…" : "Uložit změny"}</button>
      </div>
      <div className="lg:col-span-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 font-semibold">Náhled obrázku</div>
        <div className="rounded-2xl overflow-hidden bg-zinc-50 border border-zinc-200 aspect-[4/3] flex items-center justify-center">
          {form.hero_image ? <img src={form.hero_image} alt="" className="max-w-full max-h-full object-contain"/> : <div className="text-zinc-400 text-sm">Žádný obrázek</div>}
        </div>
        <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-zinc-200">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-2">Náhled titulku</div>
          <h3 className="font-display text-2xl font-bold leading-tight">
            {form.hero_title_line1} <br/>{form.hero_title_line2} <span className="gradient-text">{form.hero_title_highlight}</span> {form.hero_title_line3}
          </h3>
          <p className="text-sm text-zinc-600 mt-2">{form.hero_subtitle}</p>
        </div>
      </div>
    </form>
  );
}

function CustomLabelsEditor({ value, onChange }) {
  const [text, setText] = useState("");
  const [color, setColor] = useState("#2563EB");

  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...(value || []), { text: t, color }]);
    setText(""); setColor("#2563EB");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid="custom-labels-editor">
      <div className="flex flex-wrap gap-2 mb-3">
        {(value || []).map((l, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm" style={{ background: l.color || "#2563EB" }}>
            {l.text}
            <button type="button" onClick={() => remove(i)} className="text-white/80 hover:text-white" aria-label="Odstranit štítek">×</button>
          </span>
        ))}
        {(value || []).length === 0 && <span className="text-xs text-zinc-500">Žádné vlastní štítky.</span>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Např. AKCE -50 %"
          className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white"
          data-testid="custom-label-text-input"
        />
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="w-12 h-10 rounded-lg border border-zinc-200 cursor-pointer"
          title="Barva štítku"
          data-testid="custom-label-color-input"
        />
        <button type="button" onClick={add} className="px-4 py-2 rounded-full bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800" data-testid="custom-label-add-btn">+ Přidat</button>
      </div>
    </div>
  );
}


function HomeSections() {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        api.get("/admin/home-sections"),
        api.get("/categories"),
        api.get("/products?limit=500"),
      ]);
      setSections(s.data || []);
      setCategories(c.data || []);
      const prodList = Array.isArray(p.data) ? p.data : (p.data.items || p.data.products || []);
      setProducts(prodList);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Opravdu smazat tuto sekci?")) return;
    await api.delete(`/admin/home-sections/${id}`);
    toast.success("Smazáno");
    load();
  };

  const move = async (s, dir) => {
    const ordered = [...sections].sort((a, b) => a.order - b.order);
    const idx = ordered.findIndex((x) => x.id === s.id);
    const swap = ordered[idx + dir];
    if (!swap) return;
    const a = { ...s, order: swap.order };
    const b = { ...swap, order: s.order };
    await Promise.all([
      api.put(`/admin/home-sections/${a.id}`, a),
      api.put(`/admin/home-sections/${b.id}`, b),
    ]);
    load();
  };

  if (loading) return <div>Načítání…</div>;

  return (
    <div data-testid="admin-home-sections">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-display font-bold text-2xl">Sekce katalogu na hlavní stránce</div>
          <p className="text-sm text-zinc-600 mt-1">Vytvořte si vlastní řady produktů: podle štítku, kategorie, značky nebo ručně vybraných položek.</p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="home-section-new">+ Nová sekce</button>
      </div>

      {editing && (
        <HomeSectionForm
          initial={editing === "new" ? null : editing}
          categories={categories}
          products={products}
          onClose={() => { setEditing(null); load(); }}
        />
      )}

      <div className="space-y-3">
        {sections.length === 0 && <div className="text-center text-zinc-500 py-8 bg-zinc-50 rounded-2xl">Zatím žádné sekce. Klikněte na „+ Nová sekce".</div>}
        {sections.sort((a, b) => a.order - b.order).map((s, i) => (
          <div key={s.id} className="flex items-center gap-3 p-4 bg-white border border-zinc-200 rounded-2xl" data-testid={`home-section-row-${s.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(s, -1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 text-xs">▲</button>
              <button onClick={() => move(s, 1)} disabled={i === sections.length - 1} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 text-xs">▼</button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-zinc-900">{s.title}</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {s.filter_type === "label" && <>Štítek: <code className="px-1.5 py-0.5 bg-zinc-100 rounded">{s.filter_value || "—"}</code></>}
                {s.filter_type === "category" && <>Kategorie: <code className="px-1.5 py-0.5 bg-zinc-100 rounded">{s.filter_value || "—"}</code></>}
                {s.filter_type === "brand" && <>Značka: <code className="px-1.5 py-0.5 bg-zinc-100 rounded">{s.filter_value || "—"}</code></>}
                {s.filter_type === "manual" && <>Ruční výběr: {s.product_ids?.length || 0} produktů</>}
                {" • Max " + (s.limit || 8) + " produktů"}
                {!s.active && <span className="ml-2 px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600">Vypnuto</span>}
              </div>
            </div>
            <button onClick={() => setEditing(s)} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`home-section-edit-${s.id}`}>Upravit</button>
            <button onClick={() => remove(s.id)} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg" data-testid={`home-section-delete-${s.id}`}>Smazat</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomeSectionForm({ initial, categories, products, onClose }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    subtitle: initial?.subtitle || "",
    filter_type: initial?.filter_type || "label",
    filter_value: initial?.filter_value || "",
    product_ids: initial?.product_ids || [],
    limit: initial?.limit || 8,
    order: initial?.order ?? 0,
    active: initial?.active ?? true,
    background: initial?.background || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);

  const allLabels = (() => {
    const ls = new Set(["bestseller", "new", "sale", "lifetime", "instant_delivery", "online_activation"]);
    products.forEach((p) => {
      (p.labels || []).forEach((l) => ls.add(l));
      (p.custom_labels || []).forEach((c) => c?.text && ls.add(c.text));
    });
    return Array.from(ls);
  })();
  const allBrands = Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort();

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/home-sections/${initial.id}`, form);
      } else {
        await api.post("/admin/home-sections", form);
      }
      toast.success("Uloženo");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setSaving(false); }
  };

  const toggleProduct = (id) => {
    set("product_ids", form.product_ids.includes(id)
      ? form.product_ids.filter((x) => x !== id)
      : [...form.product_ids, id]);
  };

  return (
    <form onSubmit={save} className="bg-white border border-blue-200 rounded-2xl p-6 mb-6 space-y-4" data-testid="home-section-form">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg">{initial?.id ? "Upravit sekci" : "Nová sekce"}</div>
        <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5"/></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <FF label="Název sekce *"><input value={form.title} onChange={(e) => set("title", e.target.value)} required className={I} placeholder="Nejprodávanější" data-testid="hs-title"/></FF>
        <FF label="Podtitulek"><input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={I} placeholder="Volitelné" data-testid="hs-subtitle"/></FF>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <FF label="Typ filtru *">
          <select value={form.filter_type} onChange={(e) => set("filter_type", e.target.value)} className={I} data-testid="hs-filter-type">
            <option value="label">Podle štítku</option>
            <option value="category">Podle kategorie</option>
            <option value="brand">Podle značky</option>
            <option value="manual">Ruční výběr produktů</option>
          </select>
        </FF>
        {form.filter_type === "label" && (
          <FF label="Štítek (slug nebo název)">
            <input list="all-labels" value={form.filter_value} onChange={(e) => set("filter_value", e.target.value)} className={I} placeholder="bestseller, novinka…" data-testid="hs-filter-value-label"/>
            <datalist id="all-labels">{allLabels.map((l) => <option key={l} value={l}/>)}</datalist>
          </FF>
        )}
        {form.filter_type === "category" && (
          <FF label="Kategorie">
            <select value={form.filter_value} onChange={(e) => set("filter_value", e.target.value)} className={I} data-testid="hs-filter-value-cat">
              <option value="">— vyberte —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {categories.map((c) => <option key={`s-${c.slug}`} value={c.slug}>{c.name} ({c.slug})</option>)}
            </select>
          </FF>
        )}
        {form.filter_type === "brand" && (
          <FF label="Značka">
            <input list="all-brands" value={form.filter_value} onChange={(e) => set("filter_value", e.target.value)} className={I} placeholder="Microsoft, Adobe…" data-testid="hs-filter-value-brand"/>
            <datalist id="all-brands">{allBrands.map((b) => <option key={b} value={b}/>)}</datalist>
          </FF>
        )}
      </div>
      {form.filter_type === "manual" && (
        <FF label={`Vyberte produkty (zaškrtnuté: ${form.product_ids.length})`}>
          <div className="max-h-64 overflow-y-auto border border-zinc-200 rounded-xl p-3 space-y-1">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm py-1 hover:bg-zinc-50 px-2 rounded">
                <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)}/>
                <span className="truncate">{p.name} <span className="text-zinc-400">({p.brand})</span></span>
              </label>
            ))}
          </div>
        </FF>
      )}
      <div className="grid sm:grid-cols-3 gap-4">
        <FF label="Max počet produktů"><input type="number" min="1" max="50" value={form.limit} onChange={(e) => set("limit", parseInt(e.target.value) || 8)} className={I} data-testid="hs-limit"/></FF>
        <FF label="Pořadí (menší = výše)"><input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={I} data-testid="hs-order"/></FF>
        <FF label="Stav">
          <label className="flex items-center gap-2 mt-2.5 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="hs-active"/>
            Aktivní
          </label>
        </FF>
      </div>
      <div className="flex gap-3 justify-end pt-4 border-t border-zinc-200">
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">Zrušit</button>
        <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="hs-save">{saving ? "Ukládám…" : "Uložit"}</button>
      </div>
    </form>
  );
}


function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, "new"=create, {id,...}=edit
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);
  const dup = async (id) => {
    if (!window.confirm("Vytvořit kopii tohoto produktu?")) return;
    await api.post(`/admin/products/${id}/duplicate`); load(); toast.success("Vytvořena kopie (neaktivní – upravte ji v editoru)");
  };
  const del = async (id) => {
    if (!window.confirm("Opravdu smazat produkt?")) return;
    await api.delete(`/admin/products/${id}`); load(); toast.success("Smazáno");
  };
  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-zinc-500">{products.length} produktů</div>
        <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1" data-testid="add-product-btn"><Plus className="w-4 h-4"/>Přidat produkt</button>
      </div>
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-4">Produkt</th><th className="text-left p-4">Kategorie</th><th className="text-right p-4">Cena</th><th className="text-right p-4">Skladem</th><th className="text-right p-4">Prodáno</th><th className="text-left p-4">Stav</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100">
                <td className="p-4 font-medium">{p.name}<div className="text-xs text-zinc-400">{p.slug}</div></td>
                <td className="p-4 text-zinc-500">{p.category}</td>
                <td className="p-4 text-right tabular-nums">{formatPrice(p.price)}</td>
                <td className="p-4 text-right">{p.stock_count}</td>
                <td className="p-4 text-right">{p.sold_count}</td>
                <td className="p-4"><span className={`label-pill ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{p.active ? "Aktivní" : "Koncept"}</span></td>
                <td className="p-4 text-right flex justify-end gap-2">
                  <button onClick={() => dup(p.id)} className="text-zinc-600 hover:text-zinc-900" title="Duplikovat" data-testid={`dup-product-${p.slug}`}><Copy className="w-4 h-4"/></button>
                  <button onClick={() => setEditing(p)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-product-${p.slug}`}><Edit className="w-4 h-4"/></button>
                  <button onClick={() => del(p.id)} className="text-red-500 hover:text-red-700" data-testid={`del-product-${p.slug}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ProductForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }}/>}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    ...initial,
    images_text: (initial.images || []).join("\n"),
    features_text: (initial.features || []).join("\n"),
    tech_specs_text: Object.entries(initial.tech_specs || {}).map(([k,v]) => `${k}: ${v}`).join("\n"),
    labels: initial.labels || [],
    custom_labels: initial.custom_labels || [],
    category_ids: initial.category_ids || [],
    attributes: initial.attributes || [],
    guides: initial.guides || [],
  } : {
    slug: "", name: "", brand: "Microsoft", category: "windows",
    category_ids: [], attributes: [], guides: [],
    short_description: "", description: "",
    price: 0, price_original: null, currency: "CZK",
    images_text: "", box_mockup: "",
    features_text: "", tech_specs_text: "",
    labels: [], seo_title: "", seo_description: "", seo_keywords: "",
    rating_value: null, rating_count: null,
    active: true, is_bundle: false, bundle_items: [],
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/admin/categories").then((r) => setCategories(r.data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLabel = (l) => set("labels", form.labels.includes(l) ? form.labels.filter(x => x !== l) : [...form.labels, l]);
  const toggleCat = (id) => set("category_ids", form.category_ids.includes(id) ? form.category_ids.filter(x => x !== id) : [...form.category_ids, id]);
  const setAttr = (i, k, v) => set("attributes", form.attributes.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const addAttr = () => set("attributes", [...form.attributes, { key: "", value: "" }]);
  const delAttr = (i) => set("attributes", form.attributes.filter((_, idx) => idx !== i));
  const addGuide = (g) => set("guides", [...form.guides, { name: g.original_name || "Návod", url: g.url, size: g.size, type: g.type }]);
  const setGuideName = (i, name) => set("guides", form.guides.map((g, idx) => idx === i ? { ...g, name } : g));
  const delGuide = (i) => set("guides", form.guides.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tech_specs = {};
      form.tech_specs_text.split("\n").forEach((line) => {
        const [k, ...rest] = line.split(":");
        if (k && rest.length) tech_specs[k.trim()] = rest.join(":").trim();
      });
      const payload = {
        slug: form.slug, name: form.name, brand: form.brand, category: form.category,
        category_ids: form.category_ids,
        attributes: form.attributes.filter(a => a.key && a.value),
        guides: form.guides.filter(g => g.name && g.url),
        short_description: form.short_description, description: form.description,
        price: parseFloat(form.price), price_original: form.price_original ? parseFloat(form.price_original) : null,
        currency: form.currency,
        images: form.images_text.split("\n").map(s => s.trim()).filter(Boolean),
        box_mockup: form.box_mockup || null,
        features: form.features_text.split("\n").map(s => s.trim()).filter(Boolean),
        tech_specs,
        labels: form.labels,
        custom_labels: (form.custom_labels || []).filter((c) => c && c.text),
        seo_title: form.seo_title || "", seo_description: form.seo_description || "",
        seo_keywords: form.seo_keywords || "",
        rating_value: form.rating_value !== "" && form.rating_value != null ? parseFloat(form.rating_value) : null,
        rating_count: form.rating_count !== "" && form.rating_count != null ? parseInt(form.rating_count, 10) : null,
        active: form.active, is_bundle: form.is_bundle, bundle_items: form.bundle_items || [],
      };
      if (initial) {
        await api.put(`/admin/products/${initial.id}`, payload);
        toast.success("Produkt aktualizován");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produkt vytvořen");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setSaving(false); }
  };

  const ALL_LABELS = ["instant_delivery", "lifetime", "online_activation", "bestseller", "sale", "new"];
  const CATEGORIES = ["windows", "office", "adobe", "antivirus", "utilities", "bundle"];
  // Build category options with indentation
  const catOptions = [];
  const walk = (parentId, depth) => {
    categories.filter(c => (c.parent_id || null) === parentId).sort((a,b) => (a.order||0)-(b.order||0)).forEach((c) => {
      catOptions.push({ ...c, depth });
      walk(c.id, depth + 1);
    });
  };
  walk(null, 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="product-form-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex justify-between items-center z-10">
          <div className="font-display font-bold text-xl">{initial ? "Upravit produkt" : "Nový produkt"}</div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FF label="Slug (URL) *"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} required className={I} placeholder="windows-11-pro" data-testid="pf-slug"/></FF>
            <FF label="Název *"><input value={form.name} onChange={(e) => set("name", e.target.value)} required className={I} data-testid="pf-name"/></FF>
            <FF label="Značka"><input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={I} data-testid="pf-brand"/></FF>
            <FF label="Stará kategorie (legacy)"><select value={form.category} onChange={(e) => set("category", e.target.value)} className={I} data-testid="pf-category">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></FF>
            <FF label="Cena (Kč) *"><input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required className={I} data-testid="pf-price"/></FF>
            <FF label="Původní cena (nepovinné)"><input type="number" step="0.01" value={form.price_original || ""} onChange={(e) => set("price_original", e.target.value)} className={I} data-testid="pf-price-original"/></FF>
          </div>
          <FF label={`Kategorie – produkt může být ve více kategoriích (${form.category_ids.length} vybráno)`}>
            <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-xl p-2 bg-white" data-testid="pf-categories">
              {catOptions.length === 0 && <div className="text-xs text-zinc-500 p-2">Žádné kategorie. Vytvořte je v záložce „Kategorie".</div>}
              {catOptions.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-50 rounded text-sm cursor-pointer" style={{ paddingLeft: `${c.depth * 16 + 8}px` }}>
                  <input type="checkbox" checked={form.category_ids.includes(c.id)} onChange={() => toggleCat(c.id)} data-testid={`pf-cat-${c.slug}`}/>
                  <span>{c.name}</span>
                  <span className="text-xs text-zinc-400">/{c.slug}</span>
                </label>
              ))}
            </div>
          </FF>
          <FF label={`Filtrovatelné atributy (klíč → hodnota; ${form.attributes.length} položek)`}>
            <div className="space-y-2" data-testid="pf-attributes">
              {form.attributes.map((a, i) => (
                <div key={i} className="flex gap-2">
                  <input value={a.key} onChange={(e) => setAttr(i, "key", e.target.value)} placeholder="Platforma" className={`flex-1 ${I}`} data-testid={`pf-attr-key-${i}`}/>
                  <input value={a.value} onChange={(e) => setAttr(i, "value", e.target.value)} placeholder="Windows" className={`flex-1 ${I}`} data-testid={`pf-attr-val-${i}`}/>
                  <button type="button" onClick={() => delAttr(i)} className="text-red-500 px-2" data-testid={`pf-attr-del-${i}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
              <button type="button" onClick={addAttr} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1" data-testid="pf-attr-add"><Plus className="w-3 h-3"/>Přidat atribut</button>
            </div>
          </FF>
          <FF label={`Návody ke stažení – zobrazí se zákazníkovi v trezoru u licence (PDF/DOC/DOCX/ODT, max 25 MB; ${form.guides.length} souborů)`}>
            <div className="space-y-2" data-testid="pf-guides">
              {form.guides.map((g, i) => (
                <div key={i} className="flex gap-2 items-center bg-zinc-50 border border-zinc-200 rounded-xl p-2">
                  <FileText className="w-4 h-4 text-zinc-400 shrink-0"/>
                  <input value={g.name} onChange={(e) => setGuideName(i, e.target.value)} placeholder="Název návodu" className={`flex-1 ${I}`} data-testid={`pf-guide-name-${i}`}/>
                  <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline whitespace-nowrap" data-testid={`pf-guide-link-${i}`}>{g.size ? `${(g.size/1024/1024).toFixed(2)} MB` : "Otevřít"}</a>
                  <button type="button" onClick={() => delGuide(i)} className="text-red-500 px-2" data-testid={`pf-guide-del-${i}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
              <ImageUploader
                onUploaded={addGuide}
                label="Nahrát návod"
                accept=".pdf,.doc,.docx,.odt,.txt,.md,.rtf,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              />
            </div>
          </FF>
          <FF label="Krátký popis *"><textarea value={form.short_description} onChange={(e) => set("short_description", e.target.value)} required rows={2} className={I} data-testid="pf-short-desc"/></FF>
          <FF label="Plný popis * (formátování – tučné, kurzíva, nadpisy, barvy…)">
            <RichTextEditor value={form.description} onChange={(v) => set("description", v)} placeholder="Detailní popis produktu…" testId="pf-desc"/>
          </FF>
          <FF label="Obrázky (URL, každý na řádek)">
            <textarea value={form.images_text} onChange={(e) => set("images_text", e.target.value)} rows={3} className={`${I} font-mono text-xs`} placeholder="https://..." data-testid="pf-images"/>
            <div className="mt-1"><ImageUploader onUploaded={(f) => set("images_text", (form.images_text ? form.images_text + "\n" : "") + f.url)} label="Nahrát další"/></div>
          </FF>
          <FF label="Box mockup URL">
            <div className="flex gap-2 items-center">
              <input value={form.box_mockup || ""} onChange={(e) => set("box_mockup", e.target.value)} className={I} data-testid="pf-box"/>
              <ImageUploader onUploaded={(f) => set("box_mockup", f.url)} label="Nahrát"/>
            </div>
          </FF>
          <FF label="Funkce (každá na řádek)"><textarea value={form.features_text} onChange={(e) => set("features_text", e.target.value)} rows={4} className={I} placeholder="Trvalá licence&#10;Pro 1 PC" data-testid="pf-features"/></FF>
          <FF label="Tech. specifikace (klíč: hodnota, každý na řádek)"><textarea value={form.tech_specs_text} onChange={(e) => set("tech_specs_text", e.target.value)} rows={4} className={`${I} font-mono text-xs`} placeholder="Platforma: Windows 11&#10;Doba platnosti: Trvalá" data-testid="pf-specs"/></FF>
          <FF label="Štítky">
            <div className="flex flex-wrap gap-2">
              {ALL_LABELS.map(l => (
                <button type="button" key={l} onClick={() => toggleLabel(l)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${form.labels.includes(l) ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`} data-testid={`pf-label-${l}`}>{l}</button>
              ))}
            </div>
          </FF>
          <FF label="Vlastní štítky (libovolný text + barva)">
            <CustomLabelsEditor value={form.custom_labels || []} onChange={(v) => set("custom_labels", v)}/>
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="SEO Titulek"><input value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={I} data-testid="pf-seo-title"/></FF>
            <FF label="SEO Popis"><input value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={I} data-testid="pf-seo-desc"/></FF>
            <FF label="SEO Klíčová slova (čárkou oddělená)"><input value={form.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} className={I} data-testid="pf-seo-kw" placeholder="windows 11, klíč, licence"/></FF>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Hodnocení (0–5, pro Google ★★★★★)"><input type="number" step="0.1" min="0" max="5" value={form.rating_value ?? ""} onChange={(e) => set("rating_value", e.target.value)} className={I} data-testid="pf-rating-value" placeholder="4.8"/></FF>
              <FF label="Počet recenzí"><input type="number" min="0" value={form.rating_count ?? ""} onChange={(e) => set("rating_count", e.target.value)} className={I} data-testid="pf-rating-count" placeholder="12"/></FF>
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="pf-active"/>Aktivní</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_bundle} onChange={(e) => set("is_bundle", e.target.checked)} data-testid="pf-bundle"/>Bundle balíček</label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
          <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="pf-submit">{saving ? "Ukládám…" : (initial ? "Uložit změny" : "Vytvořit")}</button>
        </div>
      </form>
    </div>
  );
}

const I = "w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white";
function FF({ label, children }) {
  return <div><label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>{children}</div>;
}

function Keys() {
  const [keys, setKeys] = useState([]);
  const [products, setProducts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ product_id: "", key_value: "", download_url: "", activation_url: "" });
  const [csvProductId, setCsvProductId] = useState("");

  const load = () => api.get("/admin/keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); }, []);

  const addKey = async (e) => {
    e.preventDefault();
    await api.post("/admin/keys", form);
    setForm({ product_id: "", key_value: "", download_url: "", activation_url: "" });
    setShowAdd(false);
    load();
    toast.success("Klíč přidán");
  };

  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file || !csvProductId) return toast.error("Vyberte produkt a CSV");
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`/admin/keys/import-csv?product_id=${csvProductId}`, fd);
    toast.success(`Importováno ${r.data.imported} klíčů`);
    load();
  };

  const del = async (id) => { await api.delete(`/admin/keys/${id}`); load(); };

  return (
    <div data-testid="admin-keys">
      <div className="flex flex-wrap gap-3 mb-4">
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1" data-testid="add-key-btn"><Plus className="w-4 h-4"/>Přidat klíč</button>
        <a href={`${API}/admin/keys/export-csv?token=${encodeURIComponent(localStorage.getItem("ss_token") || "")}`} target="_blank" rel="noopener" className="px-4 py-2 rounded-full text-sm font-semibold border border-zinc-200 inline-flex items-center gap-1"><Download className="w-4 h-4"/>Export CSV</a>
        <div className="flex items-center gap-2 border border-zinc-200 rounded-full px-3 py-1.5">
          <select value={csvProductId} onChange={(e) => setCsvProductId(e.target.value)} className="text-sm bg-transparent" data-testid="csv-product-select">
            <option value="">Produkt pro import…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="text-sm inline-flex items-center gap-1 cursor-pointer text-blue-600 font-semibold">
            <Upload className="w-4 h-4"/>Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="csv-import-input"/>
          </label>
        </div>
      </div>
      {showAdd && (
        <form onSubmit={addKey} className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4 grid grid-cols-2 gap-3">
          <select value={form.product_id} onChange={(e) => setForm({...form, product_id: e.target.value})} required className="px-3 py-2 rounded-xl border border-zinc-200 text-sm" data-testid="key-product-select">
            <option value="">Vyberte produkt</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input value={form.key_value} onChange={(e) => setForm({...form, key_value: e.target.value})} placeholder="Licenční klíč" required className="px-3 py-2 rounded-xl border border-zinc-200 text-sm" data-testid="key-value-input"/>
          <input value={form.download_url} onChange={(e) => setForm({...form, download_url: e.target.value})} placeholder="Download URL" className="px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
          <input value={form.activation_url} onChange={(e) => setForm({...form, activation_url: e.target.value})} placeholder="Activation URL" className="px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
          <button type="submit" className="btn-primary col-span-2 py-2 rounded-full font-semibold text-sm" data-testid="key-submit">Přidat</button>
        </form>
      )}
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Produkt</th><th className="text-left p-3">Klíč</th><th className="text-left p-3">Status</th><th></th></tr>
          </thead>
          <tbody>
            {keys.slice(0, 100).map((k) => {
              const p = products.find((x) => x.id === k.product_id);
              return (
                <tr key={k.id} className="border-t border-zinc-100">
                  <td className="p-3">{p?.name || k.product_id}</td>
                  <td className="p-3 font-mono text-xs">{k.key_value}</td>
                  <td className="p-3"><span className={`label-pill ${k.status === "available" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{k.status}</span></td>
                  <td className="p-3"><button onClick={() => del(k.id)} className="text-red-500"><Trash2 className="w-4 h-4"/></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // order being edited
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const markPaid = async (id) => {
    await api.put(`/admin/orders/${id}/status?payment_status=paid`);
    load();
    toast.success("Označeno jako zaplaceno – klíče odeslány");
  };
  const cancelOrder = async (id, number) => {
    if (!window.confirm(`Opravdu stornovat objednávku ${number}? Licenční klíče se vrátí zpět do skladu.`)) return;
    try {
      const r = await api.post(`/admin/orders/${id}/cancel`);
      toast.success(`Objednávka stornována${r.data.released_keys ? ` (vráceno ${r.data.released_keys} klíč/ů)` : ""}`);
      load();
    } catch (e) {
      toast.error("Storno se nepodařilo");
    }
  };
  const statusColor = (s) => {
    if (s === "paid") return "bg-emerald-50 text-emerald-700";
    if (s === "cancelled") return "bg-zinc-200 text-zinc-600";
    return "bg-amber-50 text-amber-700";
  };
  return (
    <>
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto" data-testid="admin-orders">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Číslo</th><th className="text-left p-3">Zákazník</th><th className="text-left p-3">Platba</th><th className="text-right p-3">Částka</th><th className="text-left p-3">Stav</th><th className="text-right p-3">Akce</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-zinc-100">
                <td className="p-3 font-semibold">{o.order_number}</td>
                <td className="p-3">{o.billing?.first_name} {o.billing?.last_name}<div className="text-xs text-zinc-500">{o.email}</div></td>
                <td className="p-3 text-xs">{o.payment_method}</td>
                <td className="p-3 text-right tabular-nums">{formatPrice(o.total)}</td>
                <td className="p-3"><span className={`label-pill ${statusColor(o.payment_status)}`}>{o.payment_status}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-3">
                    {o.payment_status !== "paid" && o.payment_status !== "cancelled" && (
                      <button onClick={() => markPaid(o.id)} className="text-xs font-semibold text-blue-600 hover:text-blue-800" data-testid={`mark-paid-${o.order_number}`}>Označit zaplaceno</button>
                    )}
                    <button onClick={() => setEditing(o)} className="text-xs font-semibold text-zinc-700 hover:text-zinc-900" data-testid={`edit-order-${o.order_number}`}>Upravit</button>
                    {o.payment_status !== "cancelled" && (
                      <button onClick={() => cancelOrder(o.id, o.order_number)} className="text-xs font-semibold text-red-600 hover:text-red-800" data-testid={`cancel-order-${o.order_number}`}>Storno</button>
                    )}
                    {o.invoice_number && <a href={`${API}/orders/${o.id}/invoice.pdf?token=${encodeURIComponent(localStorage.getItem("ss_token") || "")}`} target="_blank" rel="noopener" className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1"><Download className="w-3 h-3"/>{o.invoice_number}</a>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <OrderEditModal order={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </>
  );
}

function OrderEditModal({ order, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: order.email || "",
    payment_method: order.payment_method || "",
    total: order.total ?? 0,
    notes: order.notes || "",
    billing: { ...(order.billing || {}) },
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/orders/${order.id}`, { ...form, total: parseFloat(form.total) });
      toast.success("Objednávka uložena");
      onSaved();
    } catch (err) {
      toast.error("Uložení selhalo");
    }
  };
  return (
    <div className="fixed inset-0 bg-zinc-900/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="order-edit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-xl">Upravit objednávku {order.order_number}</div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><div className="text-xs text-zinc-500 mb-1">E-mail</div><input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm" data-testid="order-edit-email"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Způsob platby</div><select value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm">
            <option value="stripe">Stripe (karta)</option>
            <option value="bank_transfer">Bankovní převod</option>
          </select></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Jméno</div><input value={form.billing.first_name || ""} onChange={(e) => setForm({...form, billing: {...form.billing, first_name: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Příjmení</div><input value={form.billing.last_name || ""} onChange={(e) => setForm({...form, billing: {...form.billing, last_name: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block col-span-2"><div className="text-xs text-zinc-500 mb-1">Firma</div><input value={form.billing.company || ""} onChange={(e) => setForm({...form, billing: {...form.billing, company: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">IČO</div><input value={form.billing.ico || ""} onChange={(e) => setForm({...form, billing: {...form.billing, ico: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">DIČ</div><input value={form.billing.dic || ""} onChange={(e) => setForm({...form, billing: {...form.billing, dic: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block col-span-2"><div className="text-xs text-zinc-500 mb-1">Adresa</div><input value={form.billing.address || ""} onChange={(e) => setForm({...form, billing: {...form.billing, address: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Město</div><input value={form.billing.city || ""} onChange={(e) => setForm({...form, billing: {...form.billing, city: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">PSČ</div><input value={form.billing.zip || ""} onChange={(e) => setForm({...form, billing: {...form.billing, zip: e.target.value}})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Celkem (Kč)</div><input type="number" step="0.01" value={form.total} onChange={(e) => setForm({...form, total: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm" data-testid="order-edit-total"/></label>
          <label className="block col-span-2"><div className="text-xs text-zinc-500 mb-1">Interní poznámka</div><textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-zinc-700 hover:bg-zinc-100">Zrušit</button>
          <button type="submit" className="btn-primary px-5 py-2 rounded-full text-sm font-semibold" data-testid="order-edit-save">Uložit změny</button>
        </div>
      </form>
    </div>
  );
}

function Customers() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setList(r.data)); }, []);
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto" data-testid="admin-customers">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500"><tr><th className="text-left p-3">Jméno</th><th className="text-left p-3">E-mail</th><th className="text-left p-3">Společnost</th><th className="text-left p-3">Registrace</th></tr></thead>
        <tbody>
          {list.map((u) => (
            <tr key={u.id} className="border-t border-zinc-100">
              <td className="p-3">{u.first_name} {u.last_name}</td>
              <td className="p-3">{u.email}</td>
              <td className="p-3">{u.company || "—"}</td>
              <td className="p-3 text-zinc-500">{new Date(u.created_at).toLocaleDateString("cs-CZ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Blog() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", content: "", cover_image: "", published: true });
  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/blog", form);
    setForm({ slug: "", title: "", excerpt: "", content: "", cover_image: "", published: true });
    load();
    toast.success("Článek publikován");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-blog">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3">
        <div className="font-display font-bold text-lg">Nový článek</div>
        <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="slug-url" required className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Název" required className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.cover_image} onChange={(e) => setForm({...form, cover_image: e.target.value})} placeholder="URL obrázku" className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <ImageUploader onUploaded={(f) => setForm({...form, cover_image: f.url})} label="Nahrát obálku"/>
        <textarea value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} placeholder="Krátký úryvek" required rows={2} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <div className="text-xs text-zinc-500 mb-1">Obsah článku (tučné, kurzíva, nadpisy, barvy, odkazy…)</div>
        <RichTextEditor value={form.content} onChange={(v) => setForm({...form, content: v})} placeholder="Napište článek…" minHeight={300} testId="blog-content-editor"/>
        <button type="submit" className="btn-primary px-4 py-2 rounded-full text-sm font-semibold">Publikovat</button>
      </form>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex justify-between">
            <div><div className="font-semibold">{p.title}</div><div className="text-xs text-zinc-500">{p.slug}</div></div>
            <button onClick={async () => { await api.delete(`/admin/blog/${p.id}`); load(); }} className="text-red-500"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Banners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", cta_text: "", cta_link: "", image_url: "", placement: "homepage_top", active: true });
  const load = () => api.get("/banners").then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/banners", form);
    load(); toast.success("Banner přidán");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-banners">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3">
        <div className="font-display font-bold text-lg">Nový banner</div>
        <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Titulek" required className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} placeholder="Podtitulek" className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})} placeholder="Tlačítko" className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.cta_link} onChange={(e) => setForm({...form, cta_link: e.target.value})} placeholder="Odkaz" className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="URL obrázku" className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/>
        <ImageUploader onUploaded={(f) => setForm({...form, image_url: f.url})} label="Nahrát obrázek"/>
        <button type="submit" className="btn-primary px-4 py-2 rounded-full text-sm font-semibold">Vytvořit</button>
      </form>
      <div className="space-y-3">
        {banners.map((b) => (
          <div key={b.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex justify-between items-center">
            <div><div className="font-semibold">{b.title}</div><div className="text-xs text-zinc-500">{b.placement}</div></div>
            <button onClick={async () => { await api.delete(`/admin/banners/${b.id}`); load(); }} className="text-red-500"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Newsletter() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setList(r.data)); }, []);
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="admin-newsletter">
      <div className="p-5 border-b border-zinc-100 font-semibold">{list.length} odběratelů</div>
      <table className="w-full text-sm">
        <tbody>
          {list.map((n) => (
            <tr key={n.id} className="border-t border-zinc-100"><td className="p-3">{n.email}</td><td className="p-3 text-right text-zinc-500 text-xs">{new Date(n.created_at).toLocaleDateString("cs-CZ")}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Categories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | {...}
  const [expanded, setExpanded] = useState({});
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggleExp = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  const del = async (id) => {
    if (!window.confirm("Smazat kategorii? Podkategorie přejdou na hlavní úroveň, produkty z ní budou odebrány.")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Kategorie smazána"); load();
  };

  const renderNode = (n, depth) => {
    const children = items.filter(c => (c.parent_id || null) === n.id);
    const hasChildren = children.length > 0;
    const open = expanded[n.id] !== false; // default open
    return (
      <div key={n.id}>
        <div className="flex items-center gap-2 py-2 px-3 border-b border-zinc-100 hover:bg-zinc-50 group" style={{ paddingLeft: `${depth * 24 + 12}px` }} data-testid={`cat-row-${n.slug}`}>
          {hasChildren ? (
            <button onClick={() => toggleExp(n.id)} className="p-0.5 hover:bg-zinc-200 rounded">{open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</button>
          ) : <span className="w-5"/>}
          <FolderTree className="w-4 h-4 text-zinc-400"/>
          <div className="flex-1">
            <span className="font-medium text-sm">{n.icon ? `${n.icon} ` : ""}{n.name}</span>
            <span className="text-xs text-zinc-400 ml-2">/{n.slug}</span>
            {!n.active && <span className="text-xs text-zinc-400 ml-2">(skryto)</span>}
          </div>
          <button onClick={() => setEditing({ ...n, _parentId: n.id })} className="opacity-0 group-hover:opacity-100 text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" title="Přidat podkategorii" data-testid={`cat-add-sub-${n.slug}`}><Plus className="w-3 h-3"/></button>
          <button onClick={() => setEditing(n)} className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-800" data-testid={`cat-edit-${n.slug}`}><Edit className="w-4 h-4"/></button>
          <button onClick={() => del(n.id)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700" data-testid={`cat-del-${n.slug}`}><Trash2 className="w-4 h-4"/></button>
        </div>
        {open && children.sort((a,b) => (a.order||0)-(b.order||0)).map(c => renderNode(c, depth + 1))}
      </div>
    );
  };

  const topLevel = items.filter(c => !c.parent_id).sort((a,b) => (a.order||0)-(b.order||0));

  return (
    <div data-testid="admin-categories">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-zinc-500">{items.length} kategorií celkem (neomezené vnoření)</div>
        <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1" data-testid="add-category-btn"><Plus className="w-4 h-4"/>Přidat hlavní kategorii</button>
      </div>
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        {topLevel.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Zatím žádné kategorie.</div>
        ) : topLevel.map(n => renderNode(n, 0))}
      </div>
      {editing && (
        <CategoryForm
          initial={editing === "new" ? null : (editing._parentId ? null : editing)}
          defaultParentId={editing !== "new" && editing._parentId ? editing._parentId : null}
          allCategories={items}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function CategoryForm({ initial, defaultParentId, allCategories, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    slug: initial.slug, name: initial.name, parent_id: initial.parent_id || "",
    order: initial.order || 0, icon: initial.icon || "", active: initial.active !== false,
  } : { slug: "", name: "", parent_id: defaultParentId || "", order: 0, icon: "", active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null, order: parseInt(form.order) || 0 };
      if (initial) {
        await api.put(`/admin/categories/${initial.id}`, payload);
        toast.success("Kategorie aktualizována");
      } else {
        await api.post("/admin/categories", payload);
        toast.success("Kategorie vytvořena");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setSaving(false); }
  };

  // build options with indentation
  const opts = [];
  const walk = (parentId, depth) => {
    allCategories.filter(c => (c.parent_id || null) === parentId)
      .sort((a,b) => (a.order||0)-(b.order||0))
      .forEach((c) => {
        if (initial && c.id === initial.id) return; // exclude self
        opts.push({ id: c.id, name: `${"— ".repeat(depth)}${c.name}` });
        walk(c.id, depth + 1);
      });
  };
  walk(null, 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="category-form-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="border-b border-zinc-200 px-6 py-4 flex justify-between items-center">
          <div className="font-display font-bold text-lg">{initial ? "Upravit kategorii" : "Nová kategorie"}</div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-4">
          <FF label="Název *"><input value={form.name} onChange={(e) => { set("name", e.target.value); if (!initial) set("slug", slugify(e.target.value)); }} required className={I} placeholder="Windows 11" data-testid="cf-name"/></FF>
          <FF label="Slug (URL) *"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} required className={I} placeholder="windows-11" data-testid="cf-slug"/></FF>
          <FF label="Nadřazená kategorie (prázdné = hlavní úroveň)">
            <select value={form.parent_id} onChange={(e) => set("parent_id", e.target.value)} className={I} data-testid="cf-parent">
              <option value="">— hlavní úroveň —</option>
              {opts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Pořadí"><input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={I} data-testid="cf-order"/></FF>
            <FF label="Ikona (emoji)"><input value={form.icon} onChange={(e) => set("icon", e.target.value)} className={I} placeholder="💻" data-testid="cf-icon"/></FF>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="cf-active"/>Aktivní (zobrazit na webu)</label>
        </div>
        <div className="border-t border-zinc-200 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
          <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="cf-submit">{saving ? "Ukládám…" : "Uložit"}</button>
        </div>
      </form>
    </div>
  );
}

function SiteHeaderFooter() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/site-settings", { params: { _: Date.now() } }).then((r) => setForm(r.data)); }, []);
  if (!form) return <div>Načítání…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", {
        brand_name: form.brand_name,
        logo_url: form.logo_url || "",
        favicon: form.favicon || "",
        header_announcement: form.header_announcement,
        footer_about: form.footer_about,
        footer_company: form.footer_company,
        footer_owner_name: form.footer_owner_name,
        footer_ic: form.footer_ic,
        footer_owner: form.footer_owner,
        footer_street: form.footer_street,
        footer_city: form.footer_city,
        footer_email: form.footer_email,
        footer_phone: form.footer_phone,
        footer_copyright: form.footer_copyright,
        bank_account: form.bank_account,
        bank_iban: form.bank_iban,
      });
      toast.success("Záhlaví a zápatí uloženo. Obnovte stránku pro náhled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="grid lg:grid-cols-2 gap-6" data-testid="admin-site">
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-4">
        <div className="font-display font-bold text-lg">Záhlaví (Header)</div>
        <FF label="Název značky (zobrazí se vedle loga; nebo místo loga když není nahráno)"><input value={form.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} className={I} placeholder="Správný Směr" data-testid="site-brand-name"/></FF>
        <FF label="Logo (obrázek – nahradí výchozí ikonku se šipkou a textem)">
          <div className="flex gap-2 items-center">
            <input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={I} placeholder="https://… nebo nahrajte" data-testid="site-logo-url"/>
            <ImageUploader onUploaded={(f) => set("logo_url", f.url)} label="Nahrát logo"/>
            {form.logo_url && <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-red-500 px-2 whitespace-nowrap" data-testid="site-logo-clear">Odstranit</button>}
          </div>
          {form.logo_url && (
            <div className="mt-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200 inline-block">
              <div className="text-xs text-zinc-500 mb-1">Náhled:</div>
              <img src={form.logo_url} alt="Logo náhled" className="h-10 max-w-[200px] object-contain" data-testid="site-logo-preview"/>
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-1">Doporučeno PNG/SVG s průhledným pozadím, výška 80–120 px. Maximum 8 MB.</p>
        </FF>
        <FF label="Favicon (ikona v záložce prohlížeče)">
          <div className="flex gap-2 items-center">
            <input value={form.favicon || ""} onChange={(e) => set("favicon", e.target.value)} className={I} placeholder="https://… nebo nahrajte" data-testid="site-favicon-url"/>
            <ImageUploader onUploaded={(f) => set("favicon", f.url)} label="Nahrát favicon"/>
            {form.favicon && <button type="button" onClick={() => set("favicon", "")} className="text-xs text-red-500 px-2 whitespace-nowrap" data-testid="site-favicon-clear">Odstranit</button>}
          </div>
          {form.favicon && (
            <div className="mt-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200 inline-flex items-center gap-3">
              <div className="text-xs text-zinc-500">Náhled:</div>
              <img src={form.favicon} alt="Favicon náhled" className="h-8 w-8 object-contain rounded" data-testid="site-favicon-preview"/>
              <img src={form.favicon} alt="Favicon náhled 16" className="h-4 w-4 object-contain"/>
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-1">Doporučeno čtvercový PNG/SVG (512×512 nebo 256×256). Změna se projeví po obnovení záložky (Ctrl+F5). Pro již otevřené záložky někdy chvíli trvá, než prohlížeč ikonu načte nově.</p>
        </FF>
        <FF label="Oznamovací pruh (nahoře přes celou šířku, prázdné = skryto)"><input value={form.header_announcement || ""} onChange={(e) => set("header_announcement", e.target.value)} className={I} placeholder="Doprava zdarma na všechny digitální produkty" data-testid="site-announcement"/></FF>
        <p className="text-xs text-zinc-500">Tip: použijte pro krátká oznámení – sleva, novinka, údržba.</p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-4">
        <div className="font-display font-bold text-lg">Zápatí (Footer)</div>
        <FF label="Popis u loga"><textarea value={form.footer_about || ""} onChange={(e) => set("footer_about", e.target.value)} rows={3} className={I} data-testid="site-footer-about"/></FF>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Název společnosti / Brand"><input value={form.footer_company || ""} onChange={(e) => set("footer_company", e.target.value)} className={I} data-testid="site-footer-company"/></FF>
          <FF label="Jméno provozovatele (na faktuře)"><input value={form.footer_owner_name || ""} onChange={(e) => set("footer_owner_name", e.target.value)} className={I} placeholder="Tomáš Němeček" data-testid="site-footer-owner-name"/></FF>
          <FF label="IČO"><input value={form.footer_ic || ""} onChange={(e) => set("footer_ic", e.target.value)} className={I} placeholder="19915535" data-testid="site-footer-ic"/></FF>
          <FF label="Provozovatel (řádek pod adresou)"><input value={form.footer_owner || ""} onChange={(e) => set("footer_owner", e.target.value)} className={I} placeholder="provozovatel: Tomáš Němeček" data-testid="site-footer-owner"/></FF>
          <FF label="Ulice a č.p."><input value={form.footer_street || ""} onChange={(e) => set("footer_street", e.target.value)} className={I} placeholder="Hutník 1419" data-testid="site-footer-street"/></FF>
          <FF label="Město a PSČ"><input value={form.footer_city || ""} onChange={(e) => set("footer_city", e.target.value)} className={I} placeholder="Veselí nad Moravou, 698 01" data-testid="site-footer-city"/></FF>
          <FF label="Kontaktní e-mail"><input value={form.footer_email || ""} onChange={(e) => set("footer_email", e.target.value)} className={I} placeholder="info@spravnysmer.eu" data-testid="site-footer-email"/></FF>
          <FF label="Telefon"><input value={form.footer_phone || ""} onChange={(e) => set("footer_phone", e.target.value)} className={I} placeholder="+420 …" data-testid="site-footer-phone"/></FF>
          <FF label="Č. bank. účtu (na fakturu)"><input value={form.bank_account || ""} onChange={(e) => set("bank_account", e.target.value)} className={I} placeholder="1234567890/0100" data-testid="site-bank-account"/></FF>
          <FF label="IBAN (na fakturu)"><input value={form.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} className={I} placeholder="CZ65 0100 0000 0012 3456 7890" data-testid="site-bank-iban"/></FF>
        </div>
        <FF label="Copyright řádek"><input value={form.footer_copyright || ""} onChange={(e) => set("footer_copyright", e.target.value)} className={I} placeholder="© 2026 Správný Směr" data-testid="site-footer-copyright"/></FF>
      </div>
      <div className="lg:col-span-2">
        <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="site-save">{saving ? "Ukládám…" : "Uložit změny"}</button>
      </div>
    </form>
  );
}

function CmsPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [faq, setFaq] = useState([]);
  const [faqSaving, setFaqSaving] = useState(false);
  const loadPages = () => api.get("/admin/pages").then((r) => setPages(r.data));
  const loadFaq = () => api.get("/site-settings", { params: { _: Date.now() } }).then((r) => setFaq(r.data.faq_items || []));
  useEffect(() => { loadPages(); loadFaq(); }, []);

  const savePage = async () => {
    await api.put(`/admin/pages/${editing.slug}`, { title: editing.title, content: editing.content });
    toast.success("Stránka uložena");
    setEditing(null); loadPages();
  };

  const setFaqItem = (i, k, v) => setFaq((f) => f.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addFaq = () => setFaq((f) => [...f, { q: "", a: "" }]);
  const delFaq = (i) => setFaq((f) => f.filter((_, idx) => idx !== i));
  const saveFaq = async () => {
    setFaqSaving(true);
    try {
      await api.put("/admin/site-settings", { faq_items: faq });
      toast.success("FAQ uloženo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba uložení");
    } finally { setFaqSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-pages">
      <div>
        <div className="font-display font-bold text-lg mb-3">Statické stránky (CMS)</div>
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left p-3">Slug</th><th className="text-left p-3">Titulek</th><th className="text-right p-3"></th></tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.slug} className="border-t border-zinc-100">
                  <td className="p-3 font-mono text-xs">/{p.slug}</td>
                  <td className="p-3 font-medium">{p.title}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing({ ...p })} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`edit-page-${p.slug}`}><Edit className="w-3 h-3"/>Upravit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <div className="font-display font-bold text-lg">FAQ – Časté dotazy</div>
          <div className="flex gap-2">
            <button onClick={addFaq} className="px-4 py-2 rounded-full text-sm font-semibold border border-zinc-200 inline-flex items-center gap-1" data-testid="faq-add"><Plus className="w-4 h-4"/>Přidat dotaz</button>
            <button onClick={saveFaq} disabled={faqSaving} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="faq-save">{faqSaving ? "Ukládám…" : "Uložit FAQ"}</button>
          </div>
        </div>
        <div className="space-y-3">
          {faq.map((it, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-2" data-testid={`faq-item-${i}`}>
              <div className="flex gap-2">
                <input value={it.q} onChange={(e) => setFaqItem(i, "q", e.target.value)} placeholder="Otázka" className={`flex-1 ${I}`} data-testid={`faq-q-${i}`}/>
                <button onClick={() => delFaq(i)} className="text-red-500 px-2" data-testid={`faq-del-${i}`}><Trash2 className="w-4 h-4"/></button>
              </div>
              <textarea value={it.a} onChange={(e) => setFaqItem(i, "a", e.target.value)} placeholder="Odpověď" rows={3} className={I} data-testid={`faq-a-${i}`}/>
            </div>
          ))}
          {faq.length === 0 && <div className="text-sm text-zinc-500 text-center py-8 bg-white rounded-2xl border border-zinc-200">Žádné FAQ položky. Přidejte první dotaz.</div>}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)} data-testid="page-edit-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex justify-between items-center z-10">
              <div className="font-display font-bold text-xl">Upravit stránku <span className="font-mono text-sm text-zinc-500">/{editing.slug}</span></div>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-zinc-100 rounded-full"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-4">
              <FF label="Titulek"><input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} className={I} data-testid="page-edit-title"/></FF>
              <FF label="Obsah (Markdown: **tučně** pro nadpis na samostatném řádku)">
                <textarea value={editing.content} onChange={(e) => setEditing({...editing, content: e.target.value})} rows={20} className={`${I} font-mono text-xs leading-relaxed`} data-testid="page-edit-content"/>
              </FF>
              <p className="text-xs text-zinc-500">Tip: řádek začínající a končící <code className="bg-zinc-100 px-1 rounded">**Nadpis**</code> se zobrazí jako podnadpis. Vnitřní <code className="bg-zinc-100 px-1 rounded">**text**</code> se zvýrazní tučně.</p>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
              <button onClick={savePage} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="page-edit-save">Uložit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Guides() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = () => api.get(`/admin/guides?_=${Date.now()}`).then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) { toast.error("Zadejte název návodu"); return; }
    try {
      if (editing.id) {
        await api.put(`/admin/guides/${editing.id}`, editing);
        toast.success("Návod uložen");
      } else {
        await api.post("/admin/guides", editing);
        toast.success("Návod vytvořen");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chyba při ukládání");
    }
  };

  const del = async (g) => {
    if (!window.confirm(`Smazat návod „${g.title}"?`)) return;
    await api.delete(`/admin/guides/${g.id}`);
    toast.success("Smazáno");
    load();
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/navod/${slug}`;
    navigator.clipboard.writeText(url);
    setCopied(slug);
    toast.success("Odkaz zkopírován");
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="space-y-5" data-testid="admin-guides">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-display font-bold text-2xl">Trezor návodů</div>
          <div className="text-sm text-zinc-500">Vytvořte návod a získejte přímý odkaz – ten můžete přidat k produktu nebo do e-mailu zákazníkovi.</div>
        </div>
        <button onClick={() => setEditing({ title: "", slug: "", content: "", excerpt: "", published: true })} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="guide-new"><Plus className="w-4 h-4"/>Nový návod</button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
            <tr><th className="text-left px-4 py-3">Název</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Stav</th><th className="text-left px-4 py-3">Odkaz</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-400 py-10">Zatím žádné návody – klikněte na „Nový návod"</td></tr>}
            {items.map(g => (
              <tr key={g.id} className="border-t border-zinc-100" data-testid={`guide-row-${g.slug}`}>
                <td className="px-4 py-3 font-medium">{g.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{g.slug}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.published ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{g.published ? "Publikováno" : "Koncept"}</span></td>
                <td className="px-4 py-3">
                  <button onClick={() => copyLink(g.slug)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline" data-testid={`guide-copy-${g.slug}`}>
                    <LinkIcon className="w-3.5 h-3.5"/>{copied === g.slug ? "Zkopírováno" : "Kopírovat link"}
                  </button>
                  <a href={`/navod/${g.slug}`} target="_blank" rel="noopener" className="ml-3 text-xs text-zinc-500 hover:text-blue-600 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3"/>Otevřít</a>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(g)} className="text-xs font-semibold text-zinc-700 hover:text-blue-600 mr-3" data-testid={`guide-edit-${g.slug}`}>Upravit</button>
                  <button onClick={() => del(g)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`guide-del-${g.slug}`}>Smazat</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex justify-between items-center">
              <div className="font-display font-bold text-lg">{editing.id ? "Upravit návod" : "Nový návod"}</div>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-3">
              <FF label="Název návodu"><input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} className={I} placeholder="Např. Jak aktivovat Windows 11 Pro" data-testid="guide-title-input"/></FF>
              <FF label="Slug (URL adresa)"><input value={editing.slug || ""} onChange={(e) => setEditing({...editing, slug: e.target.value})} className={I} placeholder="aktivace-windows-11-pro (volitelné, vygeneruje se automaticky)" data-testid="guide-slug-input"/></FF>
              <FF label="Krátký popis (volitelný)"><input value={editing.excerpt || ""} onChange={(e) => setEditing({...editing, excerpt: e.target.value})} className={I} data-testid="guide-excerpt-input"/></FF>
              <FF label="Obsah návodu (text nebo HTML)">
                <textarea value={editing.content} onChange={(e) => setEditing({...editing, content: e.target.value})} rows={14} className={I + " font-mono text-xs"} placeholder="Zde napište kroky návodu. Můžete použít HTML nebo prostý text – URL adresy se převedou na odkazy automaticky." data-testid="guide-content-input"/>
              </FF>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published !== false} onChange={(e) => setEditing({...editing, published: e.target.checked})} data-testid="guide-published"/>Publikováno (veřejně dostupné na odkazu)</label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
              <button onClick={save} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="guide-save">Uložit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ SEO Settings ============
function SeoSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-settings", { params: { _: Date.now() } }).then((r) => setData(r.data));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Normalize canonical base — ensure it starts with https://
      let canonical = (data.seo_canonical_base || "").trim().replace(/\/+$/, "");
      if (canonical && !/^https?:\/\//i.test(canonical)) {
        canonical = "https://" + canonical.replace(/^\/+/, "");
      }
      const payload = {
        seo_site_name: data.seo_site_name,
        seo_default_title: data.seo_default_title,
        seo_default_description: data.seo_default_description,
        seo_default_keywords: data.seo_default_keywords,
        seo_og_image: data.seo_og_image,
        seo_canonical_base: canonical,
        seo_twitter_handle: data.seo_twitter_handle,
        seo_ga_id: data.seo_ga_id,
        seo_gtm_id: data.seo_gtm_id,
        seo_facebook_pixel: data.seo_facebook_pixel,
        seo_locale: data.seo_locale,
        seo_robots: data.seo_robots,
      };
      await api.put("/admin/site-settings", payload);
      toast.success("SEO nastavení uloženo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba při ukládání");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div>Načítání…</div>;

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-seo">
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h2 className="font-display font-bold text-xl mb-1">Globální SEO</h2>
        <p className="text-sm text-zinc-500 mb-5">Výchozí meta tagy a Open Graph data pro celý web. Per-produktové SEO upravujte u produktu.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <FF label="Název webu (site name)"><input className={I} value={data.seo_site_name || ""} onChange={(e) => set("seo_site_name", e.target.value)} data-testid="seo-site-name"/></FF>
          <FF label="Kanonická doména (https://...)"><input className={I} value={data.seo_canonical_base || ""} onChange={(e) => set("seo_canonical_base", e.target.value)} data-testid="seo-canonical"/></FF>
          <FF label="Výchozí titulek (≤60 znaků)" full><input className={I} value={data.seo_default_title || ""} onChange={(e) => set("seo_default_title", e.target.value)} maxLength={70} data-testid="seo-title"/></FF>
          <FF label="Výchozí description (≤160 znaků)" full><textarea className={I} rows={2} value={data.seo_default_description || ""} onChange={(e) => set("seo_default_description", e.target.value)} maxLength={170} data-testid="seo-desc"/></FF>
          <FF label="Výchozí klíčová slova" full><input className={I} value={data.seo_default_keywords || ""} onChange={(e) => set("seo_default_keywords", e.target.value)} placeholder="windows licence, office klíč,..." data-testid="seo-kw"/></FF>
          <FF label="OG / sdílecí obrázek (1200×630)" full>
            <input className={I} value={data.seo_og_image || ""} onChange={(e) => set("seo_og_image", e.target.value)} placeholder="https://..." data-testid="seo-og-image"/>
            <div className="mt-2"><ImageUploader onUploaded={(r) => set("seo_og_image", r.url)} label="Nahrát OG obrázek"/></div>
            {data.seo_og_image && <img src={data.seo_og_image} alt="" className="mt-2 max-h-32 rounded-lg border border-zinc-200"/>}
          </FF>
          <FF label="Locale"><select className={I} value={data.seo_locale || "cs_CZ"} onChange={(e) => set("seo_locale", e.target.value)}><option value="cs_CZ">cs_CZ (čeština)</option><option value="sk_SK">sk_SK (slovenština)</option><option value="en_US">en_US (angličtina)</option></select></FF>
          <FF label="Robots"><select className={I} value={data.seo_robots || "index, follow"} onChange={(e) => set("seo_robots", e.target.value)}><option value="index, follow">index, follow</option><option value="noindex, nofollow">noindex, nofollow (skrýt z Google)</option></select></FF>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h2 className="font-display font-bold text-xl mb-1">Analytika & Pixely</h2>
        <p className="text-sm text-zinc-500 mb-5">Vložte ID — script se načte automaticky po souhlasu s cookies.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <FF label="Google Analytics 4 ID"><input className={I} placeholder="G-XXXXXXXXXX" value={data.seo_ga_id || ""} onChange={(e) => set("seo_ga_id", e.target.value)} data-testid="seo-ga"/></FF>
          <FF label="Google Tag Manager ID"><input className={I} placeholder="GTM-XXXXXXX" value={data.seo_gtm_id || ""} onChange={(e) => set("seo_gtm_id", e.target.value)} data-testid="seo-gtm"/></FF>
          <FF label="Meta / Facebook Pixel ID"><input className={I} placeholder="1234567890" value={data.seo_facebook_pixel || ""} onChange={(e) => set("seo_facebook_pixel", e.target.value)} data-testid="seo-fbp"/></FF>
          <FF label="Twitter / X handle"><input className={I} placeholder="@spravnysmer" value={data.seo_twitter_handle || ""} onChange={(e) => set("seo_twitter_handle", e.target.value)} data-testid="seo-twitter"/></FF>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h2 className="font-display font-bold text-xl mb-3">Technické SEO</h2>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 hover:bg-zinc-100">
            <span className="font-semibold">sitemap.xml</span><ExternalLink className="w-4 h-4"/>
          </a>
          <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 hover:bg-zinc-100">
            <span className="font-semibold">robots.txt</span><ExternalLink className="w-4 h-4"/>
          </a>
          <a href="/api/heureka/feed.xml" target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 hover:bg-zinc-100">
            <span className="font-semibold">Heuréka XML feed</span><ExternalLink className="w-4 h-4"/>
          </a>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary px-6 py-3 rounded-full font-semibold disabled:opacity-60" data-testid="seo-save">{saving ? "Ukládám…" : "Uložit SEO"}</button>
      </div>
    </div>
  );
}

// ============ Vouchers / Discount Codes ============
function Vouchers() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const load = () => api.get("/admin/vouchers").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const empty = () => ({
    code: "", type: "percent", value: 10, min_order: 0, max_uses: null,
    valid_from: "", valid_to: "", applies_to: "all", applies_ids: [], active: true, description: "",
  });

  const save = async () => {
    if (!editing.code) return toast.error("Zadejte kód");
    try {
      if (editing.id) await api.put(`/admin/vouchers/${editing.id}`, editing);
      else await api.post("/admin/vouchers", editing);
      toast.success("Uloženo");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const del = async (id) => {
    if (!confirm("Smazat slevový kód?")) return;
    await api.delete(`/admin/vouchers/${id}`);
    load();
  };

  return (
    <div data-testid="admin-vouchers">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl">Slevové kódy</h2>
          <p className="text-sm text-zinc-500">Procentuální nebo pevná sleva, omezení použití a platnosti.</p>
        </div>
        <button onClick={() => setEditing(empty())} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" data-testid="voucher-new-btn">
          <Plus className="w-4 h-4"/>Nový kód
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-3 px-4">Kód</th>
              <th className="text-left py-3 px-4">Typ</th>
              <th className="text-left py-3 px-4">Hodnota</th>
              <th className="text-left py-3 px-4">Použito</th>
              <th className="text-left py-3 px-4">Stav</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-zinc-400">Žádné kódy. Vytvořte první.</td></tr>}
            {list.map((v) => (
              <tr key={v.id} className="border-t border-zinc-100" data-testid={`voucher-row-${v.code}`}>
                <td className="py-3 px-4 font-mono font-semibold">{v.code}</td>
                <td className="py-3 px-4">{v.type === "percent" ? "Procento" : "Pevná částka"}</td>
                <td className="py-3 px-4 font-semibold">{v.type === "percent" ? `${v.value}%` : formatPrice(v.value)}</td>
                <td className="py-3 px-4 text-zinc-600">{v.used_count || 0}{v.max_uses ? ` / ${v.max_uses}` : ""}</td>
                <td className="py-3 px-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${v.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                    {v.active ? "Aktivní" : "Neaktivní"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => setEditing(v)} className="text-zinc-500 hover:text-blue-600 mr-2" aria-label="Upravit"><Edit className="w-4 h-4"/></button>
                  <button onClick={() => del(v.id)} className="text-zinc-500 hover:text-red-600" aria-label="Smazat"><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 flex justify-between items-center">
              <div className="font-display font-bold text-lg">{editing.id ? "Upravit slevový kód" : "Nový slevový kód"}</div>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-4">
              <FF label="Kód *"><input className={I} value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} placeholder="LAUNCH10" data-testid="voucher-code-input"/></FF>
              <div className="grid grid-cols-2 gap-3">
                <FF label="Typ"><select className={I} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}><option value="percent">Procento</option><option value="fixed">Pevná částka (Kč)</option></select></FF>
                <FF label={editing.type === "percent" ? "Hodnota (%)" : "Hodnota (Kč)"}><input type="number" min="0" step="any" className={I} value={editing.value} onChange={(e) => setEditing({ ...editing, value: parseFloat(e.target.value || 0) })} data-testid="voucher-value-input"/></FF>
              </div>
              <FF label="Minimální hodnota objednávky (Kč) – nepovinné"><input type="number" min="0" className={I} value={editing.min_order || 0} onChange={(e) => setEditing({ ...editing, min_order: parseFloat(e.target.value || 0) })}/></FF>
              <div className="grid grid-cols-2 gap-3">
                <FF label="Platnost od"><input type="date" className={I} value={editing.valid_from || ""} onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })}/></FF>
                <FF label="Platnost do"><input type="date" className={I} value={editing.valid_to || ""} onChange={(e) => setEditing({ ...editing, valid_to: e.target.value })}/></FF>
              </div>
              <FF label="Max. počet použití (prázdné = neomezeno)"><input type="number" min="0" className={I} value={editing.max_uses ?? ""} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value ? parseInt(e.target.value) : null })}/></FF>
              <FF label="Popisek (zobrazí se zákazníkovi)"><input className={I} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Spouštěcí sleva"/></FF>

              <FF label="Sleva platí pro">
                <select className={I} value={editing.applies_to || "all"} onChange={(e) => setEditing({ ...editing, applies_to: e.target.value, applies_ids: [] })} data-testid="voucher-applies-to">
                  <option value="all">Všechny produkty</option>
                  <option value="category">Vybrané kategorie</option>
                  <option value="product">Vybrané produkty</option>
                </select>
              </FF>

              {editing.applies_to === "category" && (
                <FF label="Kategorie (lze vybrat více, držte Ctrl/Cmd)">
                  <select multiple size={Math.min(6, Math.max(3, categories.length))} className={`${I} h-auto`} value={editing.applies_ids || []} onChange={(e) => setEditing({ ...editing, applies_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} data-testid="voucher-categories-select">
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </FF>
              )}

              {editing.applies_to === "product" && (
                <FF label="Produkty (lze vybrat více, držte Ctrl/Cmd)">
                  <select multiple size={Math.min(8, Math.max(3, products.length))} className={`${I} h-auto`} value={editing.applies_ids || []} onChange={(e) => setEditing({ ...editing, applies_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} data-testid="voucher-products-select">
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </FF>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })}/>
                Aktivní
              </label>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
              <button onClick={save} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="voucher-save-btn">Uložit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Redeem Vouchers (marketplace codes) ============
import { QRCodeSVG } from "qrcode.react";

function RedeemVouchers() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detailVoucher, setDetailVoucher] = useState(null);
  const [form, setForm] = useState({
    product_ids: [],
    note: "",
    valid_days: 180,
    count: 1,
    code: "",
    max_uses: 1,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const publicOrigin = (typeof window !== "undefined" && window.location.origin) || "https://spravnysmer.eu";

  const load = async () => {
    setLoading(true);
    try {
      const [rv, pr] = await Promise.all([
        api.get("/admin/redeem-vouchers"),
        api.get("/products"),
      ]);
      setList(rv.data || []);
      setProducts(pr.data || []);
    } catch (e) {
      toast.error("Nepodařilo se načíst poukazy");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.product_ids.length) return toast.error("Vyberte alespoň jeden produkt");
    try {
      if (form.count > 1) {
        const r = await api.post("/admin/redeem-vouchers/bulk", {
          count: form.count,
          product_ids: form.product_ids,
          note: form.note,
          valid_days: form.valid_days,
          max_uses: form.max_uses,
        });
        toast.success(`Vygenerováno ${r.data.count} poukazů`);
      } else {
        await api.post("/admin/redeem-vouchers", {
          code: form.code || undefined,
          product_ids: form.product_ids,
          note: form.note,
          valid_days: form.valid_days,
          max_uses: form.max_uses,
        });
        toast.success("Poukaz vytvořen");
      }
      setCreating(false);
      setForm({ product_ids: [], note: "", valid_days: 180, count: 1, code: "", max_uses: 1 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const del = async (id) => {
    if (!confirm("Smazat poukaz?")) return;
    try {
      await api.delete(`/admin/redeem-vouchers/${id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const revoke = async (id) => {
    if (!confirm("Zneplatnit poukaz? Zákazník ho již nebude moci uplatnit.")) return;
    try {
      await api.post(`/admin/redeem-vouchers/${id}/revoke`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Zkopírováno");
  };

  const filtered = list.filter((v) => {
    if (statusFilter !== "all" && v.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [v.code, v.note, v.redeemed_email].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: list.length,
    unused: list.filter((v) => v.status === "unused").length,
    redeemed: list.filter((v) => v.status === "redeemed").length,
    revoked: list.filter((v) => v.status === "revoked" || v.status === "expired").length,
  };

  const STATUS_LABEL = {
    unused: { text: "Nepoužitý", cls: "bg-blue-50 text-blue-700" },
    redeemed: { text: "Uplatněný", cls: "bg-emerald-50 text-emerald-700" },
    revoked: { text: "Zneplatněný", cls: "bg-zinc-100 text-zinc-500" },
    expired: { text: "Vypršelý", cls: "bg-amber-50 text-amber-700" },
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("cs-CZ"); } catch (e) { return iso.slice(0, 10); }
  };

  const redeemUrl = (code) => `${publicOrigin}/redeem/${encodeURIComponent(code)}`;

  return (
    <div data-testid="admin-redeem-vouchers">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl">Poukazy pro Aukro / marketplace</h2>
          <p className="text-sm text-zinc-500 max-w-2xl">Vygenerujte jednorázový nebo vícenásobný kód pro kupujícího. Kód mu pošlete nebo přidejte QR do popisu aukce – zákazník naskenuje mobilem a přejde přímo na <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs">spravnysmer.eu/redeem</code>.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" data-testid="redeem-new-btn">
          <Plus className="w-4 h-4"/>Nový poukaz
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MiniStat label="Celkem" value={stats.total}/>
        <MiniStat label="Nepoužité" value={stats.unused} tone="blue"/>
        <MiniStat label="Uplatněné" value={stats.redeemed} tone="emerald"/>
        <MiniStat label="Zneplatněné" value={stats.revoked} tone="zinc"/>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 mb-4 p-3 flex flex-wrap gap-2 items-center">
        <input className="px-3 py-2 rounded-xl border border-zinc-200 text-sm flex-1 min-w-[200px]" placeholder="Hledat kód / e-mail / poznámku…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="redeem-search"/>
        <select className="px-3 py-2 rounded-xl border border-zinc-200 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="redeem-status-filter">
          <option value="all">Všechny stavy</option>
          <option value="unused">Nepoužité</option>
          <option value="redeemed">Uplatněné</option>
          <option value="revoked">Zneplatněné</option>
          <option value="expired">Vypršelé</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left py-3 px-3 w-12">QR</th>
                <th className="text-left py-3 px-4">Kód</th>
                <th className="text-left py-3 px-4">Produkty</th>
                <th className="text-left py-3 px-4">Stav</th>
                <th className="text-left py-3 px-4">Použito</th>
                <th className="text-left py-3 px-4">Platnost</th>
                <th className="text-left py-3 px-4">Poznámka</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="text-center py-10 text-zinc-400">Načítání…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-zinc-400">Žádné poukazy</td></tr>}
              {!loading && filtered.map((v) => {
                const st = STATUS_LABEL[v.status] || STATUS_LABEL.unused;
                const maxUses = v.max_uses || 1;
                const usedCount = v.used_count ?? (v.status === "redeemed" ? 1 : 0);
                const usageColor = usedCount >= maxUses ? "text-emerald-700" : usedCount > 0 ? "text-blue-700" : "text-zinc-500";
                return (
                  <tr key={v.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`redeem-row-${v.code}`}>
                    <td className="py-3 px-3">
                      <button
                        onClick={() => setDetailVoucher(v)}
                        className="block rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition"
                        title="Zobrazit detail a velký QR kód"
                        data-testid={`redeem-qr-thumb-${v.code}`}
                      >
                        <QRCodeSVG value={redeemUrl(v.code)} size={40} level="M" bgColor="#ffffff" fgColor="#0f172a"/>
                      </button>
                    </td>
                    <td className="py-3 px-4 font-mono font-semibold text-xs">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setDetailVoucher(v)} className="hover:text-blue-600" data-testid={`redeem-code-${v.code}`}>{v.code}</button>
                        <button onClick={() => copy(v.code)} className="text-zinc-400 hover:text-blue-600" title="Kopírovat"><Copy className="w-3.5 h-3.5"/></button>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5">
                        {(v.products || []).map((p) => <span key={p.id} className="text-xs">{p.name}</span>)}
                      </div>
                    </td>
                    <td className="py-3 px-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`}>{st.text}</span></td>
                    <td className="py-3 px-4">
                      <span className={`text-xs font-mono font-semibold ${usageColor}`}>{usedCount}<span className="text-zinc-400">/{maxUses === 999999 ? "∞" : maxUses}</span></span>
                    </td>
                    <td className="py-3 px-4 text-zinc-600 text-xs">do {fmtDate(v.expires_at)}</td>
                    <td className="py-3 px-4 text-zinc-500 text-xs max-w-[200px] truncate" title={v.note}>{v.note || "—"}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {v.status !== "revoked" && usedCount < maxUses && (
                        <button onClick={() => revoke(v.id)} className="text-zinc-500 hover:text-amber-600 mr-2" title="Zneplatnit"><Ban className="w-4 h-4"/></button>
                      )}
                      {usedCount === 0 && (
                        <button onClick={() => del(v.id)} className="text-zinc-500 hover:text-red-600" title="Smazat"><Trash2 className="w-4 h-4"/></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 flex justify-between items-center">
              <div className="font-display font-bold text-lg">Nový poukaz</div>
              <button onClick={() => setCreating(false)} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <FF label="Produkty * (min. 1)">
                <select multiple size={Math.min(8, Math.max(3, products.length))} className={`${I} h-auto`}
                        value={form.product_ids}
                        onChange={(e) => setForm({ ...form, product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                        data-testid="redeem-products-select">
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="text-xs text-zinc-500 mt-1">Držte Ctrl/Cmd pro výběr více produktů. Při každém uplatnění zákazník obdrží 1 klíč z každého vybraného produktu.</div>
              </FF>

              <div className="grid grid-cols-3 gap-3">
                <FF label="Počet kódů">
                  <input type="number" min="1" max="500" className={I} value={form.count} onChange={(e) => setForm({ ...form, count: Math.max(1, parseInt(e.target.value) || 1) })} data-testid="redeem-count-input"/>
                </FF>
                <FF label="Uplatnění na kód">
                  <input type="number" min="1" max="9999" className={I} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Math.max(1, parseInt(e.target.value) || 1) })} data-testid="redeem-max-uses-input"/>
                </FF>
                <FF label="Platnost (dní)">
                  <input type="number" min="1" className={I} value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: Math.max(1, parseInt(e.target.value) || 180) })} data-testid="redeem-valid-days-input"/>
                </FF>
              </div>
              <div className="text-xs text-zinc-500 -mt-2 leading-relaxed">
                <strong>Uplatnění na kód</strong> = kolikrát může jeden kód uplatnit klíč (default 1). Pro balíček např. 10 klíčů pro jednoho kupujícího nastavte na <code>10</code>.
              </div>

              {form.count === 1 && (
                <FF label="Vlastní kód (nepovinné, jinak se vygeneruje)">
                  <input className={I} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Např. AUKRO-12345 (nebo nechte prázdné)"/>
                </FF>
              )}

              <FF label={'Poznámka (interní, např. „Aukro aukce #12345")'}>
                <input className={I} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Aukro aukce #…" data-testid="redeem-note-input"/>
              </FF>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
              <button onClick={create} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="redeem-create-btn">
                {form.count > 1 ? `Vygenerovat ${form.count} poukazů` : "Vytvořit poukaz"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailVoucher && (
        <RedeemDetailModal voucher={detailVoucher} publicOrigin={publicOrigin} onClose={() => setDetailVoucher(null)} onCopy={copy}/>
      )}
    </div>
  );
}

function RedeemDetailModal({ voucher, publicOrigin, onClose, onCopy }) {
  const url = `${publicOrigin}/redeem/${encodeURIComponent(voucher.code)}`;
  const maxUses = voucher.max_uses || 1;
  const usedCount = voucher.used_count ?? (voucher.status === "redeemed" ? 1 : 0);
  const remaining = Math.max(0, maxUses - usedCount);
  const redemptions = voucher.redemptions || [];

  const downloadSvg = () => {
    const svg = document.getElementById(`ss-qr-${voucher.code}`);
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const blob = new Blob([clone.outerHTML], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `poukaz-${voucher.code}.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadPng = () => {
    const svg = document.getElementById(`ss-qr-${voucher.code}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const canvas = document.createElement("canvas");
    canvas.width = 800; canvas.height = 800;
    const ctx = canvas.getContext("2d");
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `poukaz-${voucher.code}.png`;
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("cs-CZ"); } catch (e) { return iso; } };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="redeem-detail-modal">
        <div className="px-6 py-4 border-b border-zinc-200 flex justify-between items-center">
          <div>
            <div className="font-display font-bold text-lg">Detail poukazu</div>
            <div className="font-mono text-xs text-zinc-500">{voucher.code}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[75vh] overflow-y-auto">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-3">QR kód pro Aukro</div>
            <div className="border-2 border-zinc-200 rounded-2xl p-4 inline-block bg-white">
              <QRCodeSVG id={`ss-qr-${voucher.code}`} value={url} size={220} level="M" bgColor="#ffffff" fgColor="#0f172a" includeMargin={false}/>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={downloadPng} className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid="redeem-qr-download-png">
                <Download className="w-3.5 h-3.5"/> PNG
              </button>
              <button onClick={downloadSvg} className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid="redeem-qr-download-svg">
                <Download className="w-3.5 h-3.5"/> SVG
              </button>
            </div>
            <div className="mt-3 p-3 bg-zinc-50 rounded-xl">
              <div className="text-xs text-zinc-500 mb-1">URL</div>
              <div className="flex items-center gap-2">
                <code className="text-xs break-all flex-1">{url}</code>
                <button onClick={() => onCopy(url)} className="text-zinc-500 hover:text-blue-600" title="Kopírovat URL"><Copy className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatBox label="Použito" value={`${usedCount} / ${maxUses}`}/>
              <StatBox label="Zbývá" value={remaining} tone={remaining > 0 ? "emerald" : "zinc"}/>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-2">Produkty</div>
              <div className="space-y-1.5">
                {(voucher.products || []).map((p) => (
                  <div key={p.id} className="text-sm px-3 py-2 rounded-xl bg-zinc-50">{p.name}</div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-2">Historie uplatnění</div>
              {redemptions.length === 0 ? (
                <div className="text-sm text-zinc-400 italic">Zatím nebyl uplatněn</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {redemptions.map((r, i) => (
                    <div key={i} className="text-xs px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-100">
                      <div className="font-semibold text-emerald-900">{r.email}</div>
                      <div className="text-emerald-700 flex justify-between items-center gap-2">
                        <span>{fmtDate(r.at)}</span>
                        {r.ip && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-white/60 rounded" title={`IP: ${r.ip}`}>{r.ip}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {voucher.note && (
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-2">Poznámka</div>
                <div className="text-sm text-zinc-700">{voucher.note}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone = "blue" }) {
  const toneMap = { blue: "text-blue-700", emerald: "text-emerald-700", zinc: "text-zinc-500" };
  return (
    <div className="border border-zinc-200 rounded-2xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`font-display font-bold text-2xl mt-0.5 ${toneMap[tone] || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tone = "blue" }) {
  const toneMap = {
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    zinc: "text-zinc-500",
  };
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 px-5 py-4">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-bold text-3xl mt-1 ${toneMap[tone] || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

// ============ Manual Invoices (offline / Aukro sales) ============
const PAYMENT_METHOD_LABEL = {
  bank_transfer: "Bankovní převod",
  cash: "Hotově",
  stripe_offline: "Kartou (offline)",
  aukro: "Aukro",
  other: "Jiné",
};

const EMPTY_INVOICE_FORM = {
  issue_date: "",
  due_date: "",
  date_of_supply: "",
  buyer_first_name: "",
  buyer_last_name: "",
  buyer_company: "",
  buyer_address: "",
  buyer_city: "",
  buyer_zip_code: "",
  buyer_country: "Česká republika",
  buyer_ic: "",
  buyer_dic: "",
  buyer_email: "",
  buyer_phone: "",
  items: [{ name: "", quantity: 1, unit_price: 0 }],
  currency: "CZK",
  payment_method: "bank_transfer",
  paid: true,
  source: "manual",
  notes: "",
};

function ManualInvoices() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_INVOICE_FORM);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [inv, pr] = await Promise.all([
        api.get("/admin/manual-invoices"),
        api.get("/products"),
      ]);
      setList(inv.data || []);
      setProducts(pr.data || []);
    } catch (e) {
      toast.error("Nepodařilo se načíst faktury");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ ...EMPTY_INVOICE_FORM, items: [{ name: "", quantity: 1, unit_price: 0 }] });
    setCreating(true);
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { name: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  const fillItemFromProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateItem(idx, { name: p.name, unit_price: p.price });
  };

  const total = form.items.reduce((s, it) => s + Math.max(1, parseInt(it.quantity) || 1) * (parseFloat(it.unit_price) || 0), 0);

  const submit = async () => {
    if (!form.items.length || form.items.every((it) => !it.name.trim())) {
      return toast.error("Přidejte alespoň jednu položku");
    }
    const payload = {
      ...form,
      items: form.items
        .filter((it) => it.name.trim())
        .map((it) => ({
          name: it.name.trim(),
          quantity: Math.max(1, parseInt(it.quantity) || 1),
          unit_price: parseFloat(it.unit_price) || 0,
        })),
      issue_date: form.issue_date || undefined,
      due_date: form.due_date || undefined,
      date_of_supply: form.date_of_supply || undefined,
    };
    try {
      const r = await api.post("/admin/manual-invoices", payload);
      toast.success(`Faktura ${r.data.invoice_number} vytvořena`);
      const token = localStorage.getItem("ss_token") || "";
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-invoices/${r.data.id}/pdf?token=${encodeURIComponent(token)}`, "_blank");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba při vytváření faktury");
    }
  };

  const del = async (id) => {
    if (!confirm("Opravdu smazat fakturu? Účetní doporučuje pouze storno, ne smazání.")) return;
    try {
      await api.delete(`/admin/manual-invoices/${id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const togglePaid = async (inv) => {
    try {
      await api.patch(`/admin/manual-invoices/${inv.id}`, { paid: !inv.paid });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba");
    }
  };

  const openPdf = (inv) => {
    const token = localStorage.getItem("ss_token") || "";
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-invoices/${inv.id}/pdf?token=${encodeURIComponent(token)}`, "_blank");
  };

  const filtered = list.filter((v) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const buyer = v.buyer || {};
    const hay = [v.invoice_number, buyer.email, buyer.company, buyer.first_name, buyer.last_name, buyer.ic, v.notes]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const fmtCurrency = (v, ccy = "CZK") => {
    try { return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0); }
    catch (e) { return `${v} ${ccy}`; }
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("cs-CZ"); } catch (e) { return iso.slice(0, 10); }
  };
  const buyerLabel = (b) => {
    if (!b) return "—";
    if (b.company) return b.company;
    const name = `${b.first_name || ""} ${b.last_name || ""}`.trim();
    return name || b.email || "—";
  };

  const stats = {
    total: list.length,
    paid: list.filter((v) => v.paid).length,
    unpaid: list.filter((v) => !v.paid).length,
    revenue: list.filter((v) => v.paid && v.currency === "CZK").reduce((s, v) => s + (v.total || 0), 0),
  };

  return (
    <div data-testid="admin-manual-invoices">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl">Ruční faktury</h2>
          <p className="text-sm text-zinc-500 max-w-2xl">Vystavte fakturu pro prodej mimo e-shop (Aukro, převod, hotovost). Sdílí stejnou číselnou řadu <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs">FA{new Date().getFullYear()}xxxxx</code> jako objednávky z e-shopu.</p>
        </div>
        <button onClick={openCreate} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" data-testid="invoice-new-btn">
          <Plus className="w-4 h-4"/>Nová faktura
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MiniStat label="Celkem" value={stats.total}/>
        <MiniStat label="Zaplacené" value={stats.paid} tone="emerald"/>
        <MiniStat label="Neuhrazené" value={stats.unpaid} tone="zinc"/>
        <MiniStat label="Tržby CZK" value={fmtCurrency(stats.revenue, "CZK")}/>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 mb-4 p-3 flex flex-wrap gap-2 items-center">
        <input className="px-3 py-2 rounded-xl border border-zinc-200 text-sm flex-1 min-w-[200px]" placeholder="Hledat číslo / e-mail / firmu / IČ…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="invoice-search"/>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left py-3 px-4">Číslo</th>
                <th className="text-left py-3 px-4">Odběratel</th>
                <th className="text-left py-3 px-4">Datum</th>
                <th className="text-left py-3 px-4">Platba</th>
                <th className="text-right py-3 px-4">Částka</th>
                <th className="text-center py-3 px-4">Stav</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="text-center py-10 text-zinc-400">Načítání…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-zinc-400">Žádné faktury</td></tr>}
              {!loading && filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="py-3 px-4 font-mono font-semibold text-xs">
                    <button onClick={() => openPdf(inv)} className="text-blue-600 hover:underline">{inv.invoice_number}</button>
                    {inv.source === "aukro" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-orange-50 text-orange-700 font-semibold">AUKRO</span>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-xs">{buyerLabel(inv.buyer)}</div>
                    {inv.buyer?.email && <div className="text-[11px] text-zinc-500">{inv.buyer.email}</div>}
                  </td>
                  <td className="py-3 px-4 text-xs text-zinc-600">{fmtDate(inv.issue_date)}</td>
                  <td className="py-3 px-4 text-xs text-zinc-600">{PAYMENT_METHOD_LABEL[inv.payment_method] || inv.payment_method}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold text-sm">{fmtCurrency(inv.total, inv.currency)}</td>
                  <td className="py-3 px-4 text-center">
                    <button onClick={() => togglePaid(inv)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${inv.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`invoice-toggle-paid-${inv.invoice_number}`}>
                      {inv.paid ? "Zaplaceno" : "Neuhrazeno"}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openPdf(inv)} className="text-zinc-500 hover:text-blue-600 mr-2" title="PDF" data-testid={`invoice-pdf-${inv.invoice_number}`}><Download className="w-4 h-4"/></button>
                    <button onClick={() => del(inv.id)} className="text-zinc-500 hover:text-red-600" title="Smazat"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <ManualInvoiceModal
          form={form}
          setForm={setForm}
          products={products}
          onClose={() => setCreating(false)}
          onSubmit={submit}
          addItem={addItem}
          removeItem={removeItem}
          updateItem={updateItem}
          fillItemFromProduct={fillItemFromProduct}
          total={total}
        />
      )}
    </div>
  );
}

function ManualInvoiceModal({ form, setForm, products, onClose, onSubmit, addItem, removeItem, updateItem, fillItemFromProduct, total }) {
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex justify-between items-center">
          <div className="font-display font-bold text-lg">Nová faktura</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-3">Odběratel</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FF label="Firma (nepovinné)">
                <input className={I} value={form.buyer_company} onChange={(e) => upd({ buyer_company: e.target.value })} data-testid="invoice-buyer-company"/>
              </FF>
              <FF label="E-mail">
                <input type="email" className={I} value={form.buyer_email} onChange={(e) => upd({ buyer_email: e.target.value })} data-testid="invoice-buyer-email"/>
              </FF>
              <FF label="Jméno">
                <input className={I} value={form.buyer_first_name} onChange={(e) => upd({ buyer_first_name: e.target.value })} data-testid="invoice-buyer-first-name"/>
              </FF>
              <FF label="Příjmení">
                <input className={I} value={form.buyer_last_name} onChange={(e) => upd({ buyer_last_name: e.target.value })} data-testid="invoice-buyer-last-name"/>
              </FF>
              <FF label="Ulice a č.p.">
                <input className={I} value={form.buyer_address} onChange={(e) => upd({ buyer_address: e.target.value })}/>
              </FF>
              <div className="grid grid-cols-2 gap-3">
                <FF label="PSČ">
                  <input className={I} value={form.buyer_zip_code} onChange={(e) => upd({ buyer_zip_code: e.target.value })}/>
                </FF>
                <FF label="Město">
                  <input className={I} value={form.buyer_city} onChange={(e) => upd({ buyer_city: e.target.value })}/>
                </FF>
              </div>
              <FF label="Země">
                <input className={I} value={form.buyer_country} onChange={(e) => upd({ buyer_country: e.target.value })}/>
              </FF>
              <FF label="Telefon (nepovinné)">
                <input className={I} value={form.buyer_phone} onChange={(e) => upd({ buyer_phone: e.target.value })}/>
              </FF>
              <FF label="IČ">
                <input className={I} value={form.buyer_ic} onChange={(e) => upd({ buyer_ic: e.target.value })} data-testid="invoice-buyer-ic"/>
              </FF>
              <FF label="DIČ">
                <input className={I} value={form.buyer_dic} onChange={(e) => upd({ buyer_dic: e.target.value })}/>
              </FF>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-3 flex justify-between items-center">
              <span>Položky</span>
              <button onClick={addItem} className="text-blue-600 hover:text-blue-800 text-xs font-semibold inline-flex items-center gap-1" data-testid="invoice-add-item"><Plus className="w-3 h-3"/>Přidat řádek</button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-item-${idx}`}>
                  <div className="col-span-6">
                    <input list={`prods-${idx}`} className={I} placeholder="Název položky" value={it.name}
                           onChange={(e) => {
                             updateItem(idx, { name: e.target.value });
                             const p = products.find((x) => x.name === e.target.value);
                             if (p) fillItemFromProduct(idx, p.id);
                           }}
                           data-testid={`invoice-item-name-${idx}`}/>
                    <datalist id={`prods-${idx}`}>
                      {products.map((p) => <option key={p.id} value={p.name} label={`${p.price} Kč`} />)}
                    </datalist>
                  </div>
                  <input type="number" min="1" className={`${I} col-span-2 text-center`} placeholder="Ks" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`invoice-item-qty-${idx}`}/>
                  <input type="number" step="0.01" min="0" className={`${I} col-span-3 text-right`} placeholder="Cena/ks" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`invoice-item-price-${idx}`}/>
                  <button onClick={() => removeItem(idx)} disabled={form.items.length === 1} className="col-span-1 text-zinc-400 hover:text-red-600 disabled:opacity-30 pt-2.5" title="Odstranit"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Celkem</div>
                <div className="font-display font-bold text-3xl" data-testid="invoice-total-preview">{new Intl.NumberFormat("cs-CZ", { style: "currency", currency: form.currency, maximumFractionDigits: 0 }).format(total)}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 mb-3">Doklad</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FF label="Datum vystavení (výchozí: dnes)">
                <input type="date" className={I} value={form.issue_date} onChange={(e) => upd({ issue_date: e.target.value })} data-testid="invoice-issue-date"/>
              </FF>
              <FF label="DUZP">
                <input type="date" className={I} value={form.date_of_supply} onChange={(e) => upd({ date_of_supply: e.target.value })}/>
              </FF>
              <FF label="Splatnost">
                <input type="date" className={I} value={form.due_date} onChange={(e) => upd({ due_date: e.target.value })}/>
              </FF>
              <FF label="Měna">
                <select className={I} value={form.currency} onChange={(e) => upd({ currency: e.target.value })}>
                  <option value="CZK">CZK</option>
                  <option value="EUR">EUR</option>
                  <option value="HUF">HUF</option>
                  <option value="USD">USD</option>
                </select>
              </FF>
              <FF label="Způsob platby">
                <select className={I} value={form.payment_method} onChange={(e) => upd({ payment_method: e.target.value })} data-testid="invoice-payment-method">
                  <option value="bank_transfer">Bankovní převod</option>
                  <option value="cash">Hotově</option>
                  <option value="aukro">Aukro</option>
                  <option value="stripe_offline">Kartou (offline)</option>
                  <option value="other">Jiné</option>
                </select>
              </FF>
              <FF label="Zdroj">
                <select className={I} value={form.source} onChange={(e) => upd({ source: e.target.value })}>
                  <option value="manual">Ručně</option>
                  <option value="aukro">Aukro</option>
                </select>
              </FF>
            </div>
            <div className="mt-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.paid} onChange={(e) => upd({ paid: e.target.checked })} className="w-4 h-4" data-testid="invoice-paid-checkbox"/>
                <span>Faktura je již <strong>zaplacena</strong> (přidá se razítko „ZAPLACENO")</span>
              </label>
            </div>
            <FF label="Poznámka (nepovinné)">
              <textarea rows={2} className={I} value={form.notes} onChange={(e) => upd({ notes: e.target.value })} placeholder="Např. Prodej přes Aukro aukce #12345"/>
            </FF>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Zrušit</button>
          <button onClick={onSubmit} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" data-testid="invoice-submit-btn">
            <Receipt className="w-4 h-4"/>Vystavit fakturu
          </button>
        </div>
      </div>
    </div>
  );
}



// ============ Email Logs ============
function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-logs").then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testEmail) return toast.error("Zadejte e-mail");
    setSending(true);
    try {
      const r = await api.post("/admin/email-test", { to: testEmail });
      if (r.data.status === "sent") {
        toast.success(`Test odeslán (${r.data.id || ""}). Zkontrolujte schránku včetně spamu.`);
      } else if (r.data.status === "logged_only") {
        toast.error("RESEND_API_KEY není nastavený. E-mail jen zalogován.");
      } else {
        toast.error("Chyba: " + (r.data.error || "neznámá"));
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba odeslání");
    } finally {
      setSending(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      sent: { c: "bg-emerald-50 text-emerald-700", t: "Odesláno" },
      failed: { c: "bg-red-50 text-red-700", t: "Chyba" },
      logged_only: { c: "bg-amber-50 text-amber-700", t: "Jen log (chybí klíč)" },
      queued: { c: "bg-zinc-100 text-zinc-600", t: "Ve frontě" },
    };
    const v = map[s] || { c: "bg-zinc-100 text-zinc-600", t: s };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${v.c}`}>{v.t}</span>;
  };

  return (
    <div data-testid="admin-emails">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-2xl">E-mail logy</h2>
          <p className="text-sm text-zinc-500">Posledních {logs.length} odeslání. Status, chyby a Reply-To.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-xs font-semibold text-zinc-600">Otestovat odeslání</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="vas@email.cz"
              className="block px-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-64"
              data-testid="email-test-input"
            />
          </div>
          <button
            onClick={sendTest}
            disabled={sending || !testEmail}
            className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
            data-testid="email-test-btn"
          >
            {sending ? "Posílám…" : "Odeslat test"}
          </button>
          <button onClick={load} className="px-4 py-2.5 rounded-full text-sm font-semibold border border-zinc-200 hover:bg-zinc-50">Obnovit</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-3 px-4">Příjemce</th>
              <th className="text-left py-3 px-4">Předmět</th>
              <th className="text-left py-3 px-4">Stav</th>
              <th className="text-left py-3 px-4">Chyba / Provider ID</th>
              <th className="text-left py-3 px-4">Čas</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="text-center py-10 text-zinc-400">Načítání…</td></tr>}
            {!loading && logs.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-zinc-400">Žádné záznamy</td></tr>}
            {logs.map((l, idx) => (
              <tr key={idx} className="border-t border-zinc-100 align-top">
                <td className="py-3 px-4 font-medium">{l.to_email}</td>
                <td className="py-3 px-4 text-zinc-700">{l.subject}</td>
                <td className="py-3 px-4">{statusBadge(l.status)}</td>
                <td className="py-3 px-4 font-mono text-xs text-zinc-500 max-w-md break-words">{l.error || l.provider_id || "—"}</td>
                <td className="py-3 px-4 text-xs text-zinc-500 whitespace-nowrap">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-900">
        <p className="font-semibold mb-1">Tipy pro doručitelnost:</p>
        <ul className="list-disc list-inside space-y-1 text-blue-800">
          <li>Doména <code className="px-1 bg-blue-100 rounded">spravnysmer.eu</code> musí být ověřena v Resendu (DNS záznamy SPF, DKIM, případně DMARC).</li>
          <li>SENDER_EMAIL musí používat ověřenou doménu (např. <code className="px-1 bg-blue-100 rounded">noreply@spravnysmer.eu</code>).</li>
          <li>Pokud test vrátí <b>Chyba</b>, podívejte se na text chyby vpravo — to je odpověď ze Resendu.</li>
          <li>Pokud test ukáže <b>Jen log</b>, RESEND_API_KEY není v prostředí. Po doplnění proveďte redeploy.</li>
        </ul>
      </div>
    </div>
  );
}


function DownloadVault() {
  const [files, setFiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [meta, setMeta] = useState({ title: "", version: "", notes: "", product_ids: [] });

  const fmt = (n) => {
    if (!n) return "0 B";
    const u = ["B","KB","MB","GB","TB"]; let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
  };

  const load = () => api.get("/admin/files").then((r) => setFiles(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const SMALL = 90 * 1024 * 1024;          // 90 MB → proxy through backend
      const SINGLE_PUT_LIMIT = 100 * 1024 * 1024; // 100 MB → single PUT presigned
      if (file.size <= SMALL) {
        // Proxy upload through backend (simpler, no CORS dance)
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", meta.title || file.name);
        fd.append("version", meta.version || "");
        fd.append("notes", meta.notes || "");
        fd.append("product_ids", (meta.product_ids || []).join(","));
        await api.post("/admin/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || file.size))),
        });
      } else if (file.size <= SINGLE_PUT_LIMIT) {
        // Single PUT presigned (90–100 MB)
        const { data: pres } = await api.post("/admin/files/presign-upload", {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
        });
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", pres.upload_url, true);
          xhr.setRequestHeader("Content-Type", pres.content_type);
          xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setProgress(Math.round((ev.loaded * 100) / ev.total)); };
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`R2 PUT ${xhr.status}`));
          xhr.onerror = () => reject(new Error("Síťová chyba při uploadu do R2 (zkontroluj CORS bucketu)"));
          xhr.send(file);
        });
        await api.post("/admin/files/finalize", {
          r2_key: pres.key, filename: file.name,
          title: meta.title || file.name, version: meta.version, notes: meta.notes,
          product_ids: meta.product_ids,
        });
      } else {
        // === Multipart upload for files > 100 MB ===
        const { data: init } = await api.post("/admin/files/multipart/start", {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
        });
        const partSize = init.part_size || 50 * 1024 * 1024;
        const totalParts = Math.ceil(file.size / partSize);
        const completedParts = [];
        let uploadedBytes = 0;
        try {
          for (let i = 0; i < totalParts; i++) {
            const partNumber = i + 1;
            const start = i * partSize;
            const end = Math.min(start + partSize, file.size);
            const blob = file.slice(start, end);
            const { data: sig } = await api.post("/admin/files/multipart/sign-part", {
              key: init.key, upload_id: init.upload_id, part_number: partNumber,
            });
            // PUT this chunk and capture ETag
            const etag = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("PUT", sig.upload_url, true);
              xhr.upload.onprogress = (ev) => {
                if (ev.lengthComputable) {
                  const cur = uploadedBytes + ev.loaded;
                  setProgress(Math.round((cur * 100) / file.size));
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  const tag = (xhr.getResponseHeader("ETag") || "").replace(/"/g, "");
                  resolve(tag);
                } else reject(new Error(`Part ${partNumber}: HTTP ${xhr.status}`));
              };
              xhr.onerror = () => reject(new Error(`Part ${partNumber}: síťová chyba (zkontroluj CORS bucketu)`));
              xhr.send(blob);
            });
            completedParts.push({ PartNumber: partNumber, ETag: etag });
            uploadedBytes += (end - start);
            setProgress(Math.round((uploadedBytes * 100) / file.size));
          }
          await api.post("/admin/files/multipart/complete", {
            key: init.key, upload_id: init.upload_id, parts: completedParts,
          });
        } catch (mpErr) {
          // Best-effort cleanup
          api.post("/admin/files/multipart/abort", { key: init.key, upload_id: init.upload_id }).catch(() => {});
          throw mpErr;
        }
        await api.post("/admin/files/finalize", {
          r2_key: init.key, filename: file.name,
          title: meta.title || file.name, version: meta.version, notes: meta.notes,
          product_ids: meta.product_ids,
        });
      }
      toast.success(`Nahráno: ${file.name}`);
      setMeta({ title: "", version: "", notes: "", product_ids: [] });
      load();
    } catch (err) {
      console.error(err);
      toast.error(`Upload selhal: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Smazat soubor "${name}"? Tato akce je nevratná.`)) return;
    await api.delete(`/admin/files/${id}`);
    toast.success("Soubor smazán");
    load();
  };

  const copyLink = async (id) => {
    const { data } = await api.get(`/admin/files/${id}/download`);
    const landingUrl = data.slug ? `${window.location.origin}/download/${data.slug}` : data.url;
    navigator.clipboard.writeText(landingUrl);
    toast.success(data.slug ? "Odkaz na landing page zkopírován" : "Odkaz zkopírován");
  };

  const saveEdit = async () => {
    await api.put(`/admin/files/${editing.id}`, {
      title: editing.title, version: editing.version, notes: editing.notes,
      slug: editing.slug || "",
      product_ids: editing.product_ids || [],
    });
    toast.success("Uloženo");
    setEditing(null);
    load();
  };

  const productName = (id) => products.find((p) => p.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="admin-downloads">
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-lg">Nahrát nový soubor</h3>
            <p className="text-xs text-zinc-500 mt-1">Instalačky, ISO, ZIPy, dokumentace. Soubory do 90 MB jdou přes server, větší (ISO) přímo do Cloudflare R2.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Název (volitelně, jinak název souboru)</div><input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm" placeholder="Office 2021 ProPlus – instalace"/></label>
          <label className="block"><div className="text-xs text-zinc-500 mb-1">Verze</div><input value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm" placeholder="2021 LTSC / 2024.5"/></label>
          <label className="block md:col-span-2"><div className="text-xs text-zinc-500 mb-1">Přiřadit k produktům (Ctrl/Cmd pro výběr více)</div>
            <select multiple size={Math.min(6, Math.max(3, products.length))} value={meta.product_ids} onChange={(e) => setMeta({ ...meta, product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm">
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2"><div className="text-xs text-zinc-500 mb-1">Interní poznámka</div><textarea rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
        </div>
        <label className="mt-4 flex items-center gap-3 cursor-pointer">
          <span className="btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Download className="w-4 h-4"/>{uploading ? `Nahrávám… ${progress}%` : "Vybrat soubor"}
          </span>
          <input type="file" onChange={handleFile} disabled={uploading} className="hidden" data-testid="download-vault-file-input"/>
          {uploading && (
            <div className="flex-1 max-w-md">
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }}/>
              </div>
            </div>
          )}
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Soubor</th><th className="text-left p-3">Verze</th><th className="text-left p-3">Velikost</th><th className="text-left p-3">Produkty</th><th className="text-right p-3">Stažení</th><th className="text-right p-3">Akce</th></tr>
          </thead>
          <tbody>
            {files.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Zatím žádné soubory.</td></tr>}
            {files.map((f) => (
              <tr key={f.id} className="border-t border-zinc-100">
                <td className="p-3">
                  <div className="font-semibold">{f.title}</div>
                  <div className="text-xs text-zinc-500">{f.filename}</div>
                  {f.slug ? <div className="text-[11px] text-blue-600 font-mono mt-0.5">/download/{f.slug}</div> : null}
                </td>
                <td className="p-3 text-xs">{f.version || "—"}</td>
                <td className="p-3 text-xs tabular-nums">{fmt(f.size)}</td>
                <td className="p-3 text-xs">
                  {(f.product_ids || []).length === 0
                    ? <span className="text-zinc-400">— nepřiřazeno —</span>
                    : <div className="flex flex-wrap gap-1">{(f.product_ids || []).slice(0, 3).map((id) => <span key={id} className="label-pill bg-blue-50 text-blue-700">{productName(id)}</span>)}{(f.product_ids || []).length > 3 && <span className="text-zinc-500">+{f.product_ids.length - 3}</span>}</div>}
                </td>
                <td className="p-3 text-right tabular-nums">{f.download_count || 0}×</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-3">
                    <button onClick={() => copyLink(f.id)} className="text-xs font-semibold text-blue-600" data-testid={`copy-link-${f.id}`}>Test odkaz</button>
                    <button onClick={() => setEditing({ ...f, product_ids: f.product_ids || [] })} className="text-xs font-semibold text-zinc-700">Upravit</button>
                    <button onClick={() => remove(f.id, f.title)} className="text-xs font-semibold text-red-600" data-testid={`delete-file-${f.id}`}>Smazat</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-zinc-900/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-xl">Upravit soubor</div>
              <button onClick={() => setEditing(null)} className="text-zinc-500 text-2xl">×</button>
            </div>
            <label className="block"><div className="text-xs text-zinc-500 mb-1">Název</div><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
            <label className="block"><div className="text-xs text-zinc-500 mb-1">Verze</div><input value={editing.version || ""} onChange={(e) => setEditing({ ...editing, version: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
            <label className="block"><div className="text-xs text-zinc-500 mb-1">Produkty</div>
              <select multiple size={Math.min(8, Math.max(3, products.length))} value={editing.product_ids} onChange={(e) => setEditing({ ...editing, product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm">
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block"><div className="text-xs text-zinc-500 mb-1">Poznámka</div><textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"/></label>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-zinc-700 hover:bg-zinc-100">Zrušit</button>
              <button onClick={saveEdit} className="btn-primary px-5 py-2 rounded-full text-sm font-semibold">Uložit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

